;(function () {
  "use strict";

  var VERSION = "1.6.1";
  var DEFAULT_API_ENDPOINT = "https://edge.tiquo.app";
  var DEFAULT_PHONE_FLAG_BASE_URL = "https://www.tiquo.app/flags";
  // Services and categories carry up to 15 gallery images.
  var MAX_GALLERY_IMAGES = 15;
  var ANALYTICS_LOCATION_EVENT = "tiquo:locationchange";
  var ANALYTICS_SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  // How long a client-side navigation's pageview waits before reading
  // document.title. Routers call history.pushState before they set the new
  // title, and the first title change isn't always the final one: the Next.js
  // 16 App Router sets "" right after pushState and the real title 150-300ms
  // later. The pageview is sent sooner if the page is hidden or navigates again.
  var PAGEVIEW_TITLE_DELAY_MS = 1000;
  var IFRAME_AUTH_READY = "tiquo:iframe-auth-ready";
  var IFRAME_AUTH_RESPONSE = "tiquo:iframe-auth-response";
  var IFRAME_AUTH_PING = "tiquo:iframe-auth-ping";
  var IFRAME_AUTH_VERSION = 1;
  var FLOW_AVAILABILITY_MAX_DAYS = 7;
  // Payment-method embed events (mirrors packages/dom-package/src/payment-methods.ts
  // and tiquo-customer src/lib/saved-payment-methods.ts).
  var PAYMENT_METHOD_EVENT_VERSION = 1;
  var PAYMENT_METHOD_EVENTS = {
    "tiquo:payment-method-saved": "saved",
    "tiquo:payment-method-removed": "removed",
    "tiquo:payment-method-default-changed": "default-changed",
  };
  var script = document.currentScript;
  var globalConfig = window.TiquoConfig || {};
  var endpointOverride =
    getScriptAttr("data-tiquo-api-endpoint") ||
    getScriptAttr("data-api-endpoint") ||
    globalConfig.apiEndpoint ||
    "";
  var publicKey =
    getScriptAttr("data-tiquo-public-key") ||
    getScriptAttr("data-public-key") ||
    globalConfig.publicKey ||
    "";
  var apiEndpoint = isLocalDevelopmentHost() && endpointOverride
    ? normalizeApiEndpoint(endpointOverride)
    : DEFAULT_API_ENDPOINT;
  var debug = readBoolean(
    getScriptAttr("data-tiquo-debug") || globalConfig.debug,
    false,
  );
  var analyticsEnabled = readBoolean(
    getScriptAttr("data-tiquo-analytics") || globalConfig.analytics,
    true,
  );
  var storagePrefix =
    getScriptAttr("data-tiquo-storage-prefix") ||
    globalConfig.storagePrefix ||
    "tiquo_auth_";
  var consentStorageKey = "tiquo_consent_" + publicKey;
  var consentMode = normalizeConsentMode(
    safeStorage(window.localStorage, consentStorageKey) ||
    getScriptAttr("data-tiquo-consent") ||
    globalConfig.consent ||
    (analyticsEnabled ? "full" : "none"),
  );
  var listeners = [];
  var readyCallbacks = [];
  var accessToken = null;
  var refreshToken = null;
  var session = null;
  var refreshTimer = null;
  var refreshPromise = null;
  var authReady = false;
  var sessionSyncTimer = null;
  var lastPath = null;
  var pageStartedAt = Date.now();
  // Sends the pageview that is waiting for the title, if there is one.
  var pendingPageview = null;
  // Set once an engagement is sent; cleared on a new pageview or when the page
  // is visible again.
  var engagementSent = false;
  var walletCardLinksCache = {};
  var membershipDataPromise = null;
  var membershipDataToken = null;
  var guestFlowDataPromise = null;
  var guestFlowDataToken = null;
  var flowServiceDataPromises = {};
  var paymentMethodDataPromise = null;
  var paymentMethodDataKey = null;
  var paymentMethodsRevision = 0;
  var paymentMethodListenerInstalled = false;
  var mutationObserver = null;
  var iframeObserver = null;
  var iframeBridges = [];
  var lastIframePingAccessToken = null;
  var guestPassRevision = 0;
  var passBalanceRenderKey = null;
  var renderScheduled = false;
  var analyticsInitialized = false;
  var phoneFieldObserver = null;

  var Tiquo = {
    version: VERSION,
    config: {
      publicKey: publicKey,
      apiEndpoint: apiEndpoint,
      analytics: analyticsEnabled,
    },
    ready: ready,
    track: track,
    identify: identify,
    sendOTP: sendOTP,
    verifyOTP: verifyOTP,
    getUser: getUser,
    updateProfile: updateProfile,
    createEnquiry: createEnquiry,
    subscribeToMarketing: subscribeToMarketing,
    uploadProfilePhoto: uploadProfilePhoto,
    isAuthenticated: isAuthenticated,
    logout: logout,
    getOrders: getOrders,
    getBookings: getBookings,
    cancelBooking: cancelBooking,
    getBookingRescheduleOptions: getBookingRescheduleOptions,
    rescheduleBooking: rescheduleBooking,
    getMemberships: getMemberships,
    getPermissions: getPermissions,
    hasPermission: hasPermission,
    getGuestPasses: getGuestPasses,
    getPassBalances: getPassBalances,
    getGuestFlows: getGuestFlows,
    getGuestFlow: getGuestFlow,
    createGuestInvitations: createGuestInvitations,
    getGuestInvitation: getGuestInvitation,
    getGuestInvitations: getGuestInvitations,
    acceptGuestInvitation: acceptGuestInvitation,
    getPolicies: getPolicies,
    getPolicy: getPolicy,
    getFlowServices: getFlowServices,
    getFlowAvailability: getFlowAvailability,
    getFlowService: getFlowService,
    getWalletCardLinks: getWalletCardLinks,
    getPaymentMethods: getPaymentMethods,
    setDefaultPaymentMethod: setDefaultPaymentMethod,
    removePaymentMethod: removePaymentMethod,
    getUpcomingBookings: function (options) {
      return getBookings(extend({}, options || {}, { range: "upcoming" }));
    },
    refresh: refreshSession,
    getIframeToken: getIframeToken,
    embedCustomerFlow: embedCustomerFlow,
    embedServiceBooking: embedServiceBooking,
    embedMembership: embedMembership,
    embedPaymentMethods: embedPaymentMethods,
    embedCardForm: embedCardForm,
    createPhoneNumberField: createPhoneNumberField,
    phoneNumberField: {
      create: createPhoneNumberField,
      getCountries: getPhoneCountries,
      normalize: normalizePhoneNumber,
      validate: validatePhoneNumber,
    },
    render: forceRenderAttributes,
    onAuthStateChange: onAuthStateChange,
    initWithTokens: initWithTokens,
    setConsent: setConsent,
    printBranding: function () {
      printBranding(true);
    },
    getConsent: function () {
      return consentMode;
    },
  };

  window.Tiquo = Tiquo;
  window.tiquo = Tiquo;
  installVisibilityStyles();
  installPhoneNumberFieldStyles();
  setAuthReady(false);
  printBranding(false);

  onReady(function () {
    setTimeout(function () {
      bindPhoneNumberFields();
      observePhoneNumberFields();
    }, 0);
  });

  if (!publicKey) {
    log("Missing public key. Add data-tiquo-public-key to the script tag.");
    setAuthReady(true);
  } else {
    restoreTokens();
    installSessionLifecycleListeners();
    autoInjectIframeTokens();

    if (canTrackAnalytics()) {
      initAnalytics();
    }

    onReady(function () {
      refreshSession().finally(function () {
        setAuthReady(true);
        renderAttributes();
        runReadyCallbacks();
        observeDom();
      });
    });
  }

  function getScriptAttr(name) {
    return script && script.getAttribute ? script.getAttribute(name) : null;
  }

  function readBoolean(value, fallback) {
    if (value === undefined || value === null || value === "") return fallback;
    if (typeof value === "boolean") return value;
    return !/^(0|false|no|off)$/i.test(String(value));
  }

  var PHONE_COUNTRY_DIAL_CODES = "US:1,CA:1,AG:1268,AI:1264,AS:1684,BB:1246,BM:1441,BS:1242,DM:1767,DO:1809,GD:1473,GU:1671,JM:1876,KN:1869,KY:1345,LC:1758,MP:1670,MS:1664,PR:1787,SX:1721,TC:1649,TT:1868,VC:1784,VG:1284,VI:1340,GB:44,IE:353,FR:33,DE:49,IT:39,ES:34,PT:351,NL:31,BE:32,LU:352,CH:41,AT:43,SE:46,NO:47,DK:45,FI:358,IS:354,PL:48,CZ:420,SK:421,HU:36,RO:40,BG:359,HR:385,SI:386,RS:381,BA:387,ME:382,MK:389,AL:355,XK:383,GR:30,CY:357,MT:356,TR:90,LT:370,LV:371,EE:372,MD:373,UA:380,BY:375,RU:7,GE:995,AM:374,AZ:994,MC:377,AD:376,SM:378,LI:423,VA:379,GI:350,FO:298,GL:299,CN:86,JP:81,KR:82,KP:850,IN:91,PK:92,BD:880,LK:94,NP:977,AF:93,MM:95,TH:66,VN:84,KH:855,LA:856,MY:60,SG:65,ID:62,PH:63,BN:673,TL:670,TW:886,HK:852,MO:853,MN:976,KZ:7,UZ:998,TM:993,KG:996,TJ:992,MV:960,BT:975,AE:971,SA:966,QA:974,KW:965,BH:973,OM:968,YE:967,IR:98,IQ:964,JO:962,LB:961,SY:963,IL:972,PS:970,ZA:27,NG:234,KE:254,GH:233,EG:20,MA:212,DZ:213,TN:216,LY:218,ET:251,TZ:255,UG:256,RW:250,CD:243,CG:242,CI:225,SN:221,CM:237,MZ:258,AO:244,ZW:263,ZM:260,BW:267,NA:264,MU:230,MG:261,MW:265,ML:223,BF:226,NE:227,TD:235,SD:249,SS:211,SO:252,DJ:253,ER:291,GM:220,GN:224,GW:245,LR:231,SL:232,TG:228,BJ:229,GA:241,GQ:240,ST:239,CF:236,CV:238,MR:222,BI:257,LS:266,SZ:268,KM:269,SC:248,RE:262,YT:262,MX:52,BR:55,AR:54,CO:57,CL:56,PE:51,VE:58,EC:593,UY:598,PY:595,BO:591,GY:592,SR:597,GF:594,FK:500,CR:506,PA:507,GT:502,HN:504,SV:503,NI:505,BZ:501,CU:53,HT:509,GP:590,MQ:596,PM:508,AU:61,NZ:64,FJ:679,PG:675,WS:685,TO:676,VU:678,SB:677,NC:687,PF:689,CK:682,NU:683,NR:674,TV:688,KI:686,MH:692,FM:691,PW:680";
  var phoneCountriesCache = null;
  var phoneCountriesByDialCodeCache = null;

  function normalizePhoneCountryCode(value) {
    return String(value || "").trim().toUpperCase();
  }

  function getPhoneCountryFlagUrl(code, flagBaseUrl) {
    var normalized = normalizePhoneCountryCode(code);
    var baseUrl = String(flagBaseUrl || DEFAULT_PHONE_FLAG_BASE_URL).replace(/\/+$/, "");
    return baseUrl + "/" + normalized.toLowerCase() + ".svg";
  }

  function getPhoneCountryName(code) {
    try {
      if (typeof Intl !== "undefined" && Intl.DisplayNames) {
        var locale =
          (document.documentElement && document.documentElement.lang) ||
          navigator.language ||
          "en";
        return new Intl.DisplayNames([locale], { type: "region" }).of(code) || code;
      }
    } catch {
      // Fall back to the ISO code.
    }
    return code;
  }

  function getPhoneCountries() {
    if (!phoneCountriesCache) {
      phoneCountriesCache = PHONE_COUNTRY_DIAL_CODES.split(",")
        .map(function (entry) {
          var parts = entry.split(":");
          return {
            code: parts[0],
            name: getPhoneCountryName(parts[0]),
            dialCode: "+" + parts[1],
            flagUrl: getPhoneCountryFlagUrl(parts[0]),
          };
        })
        .sort(function (a, b) {
          return a.name.localeCompare(b.name);
        });
    }
    return phoneCountriesCache.map(function (country) {
      return extend({}, country);
    });
  }

  function getPhoneCountry(code, countries) {
    var normalized = normalizePhoneCountryCode(code);
    for (var i = 0; i < countries.length; i += 1) {
      if (countries[i].code === normalized) return countries[i];
    }
    return null;
  }

  function getPhoneCountriesByDialCode() {
    if (!phoneCountriesByDialCodeCache) {
      if (!phoneCountriesCache) getPhoneCountries();
      phoneCountriesByDialCodeCache = phoneCountriesCache.slice().sort(function (a, b) {
        return b.dialCode.length - a.dialCode.length;
      });
    }
    return phoneCountriesByDialCodeCache;
  }

  function getPhoneCountryFromNumber(value, countries) {
    var normalized = normalizePhoneNumber(value);
    if (!normalized || normalized === "+") return null;
    var digits = normalized.slice(1);
    var sorted = countries.length === phoneCountriesCache.length
      ? getPhoneCountriesByDialCode()
      : countries.slice().sort(function (a, b) {
          return b.dialCode.length - a.dialCode.length;
        });
    for (var i = 0; i < sorted.length; i += 1) {
      if (digits.indexOf(sorted[i].dialCode.slice(1)) === 0) return sorted[i];
    }
    return null;
  }

  function normalizePhoneNumber(value) {
    var input = String(value || "")
      .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF\u00AD\u2028\u2029\u061C\u180E]/g, "")
      .replace(/\(0\)/g, "")
      .trim();
    if (!input) return "";
    var hasPlus = input.charAt(0) === "+";
    var digits = input.replace(/\D/g, "");
    if (!hasPlus && digits.indexOf("00") === 0 && digits.length > 9) {
      digits = digits.slice(2);
    }
    if (!digits) return "";
    var countries = getPhoneCountriesByDialCode();
    for (var i = 0; i < countries.length; i += 1) {
      var dialDigits = countries[i].dialCode.slice(1);
      if (digits.indexOf(dialDigits) !== 0) continue;
      if (digits.charAt(dialDigits.length) === "0") {
        var withoutDomesticPrefix =
          digits.slice(0, dialDigits.length) + digits.slice(dialDigits.length + 1);
        if (withoutDomesticPrefix.length >= 7 && withoutDomesticPrefix.length <= 15) {
          digits = withoutDomesticPrefix;
        }
      }
      break;
    }
    return "+" + digits;
  }

  function validatePhoneNumber(value) {
    var rawValue = String(value || "").trim();
    var normalized = normalizePhoneNumber(value);
    var digits = normalized.slice(1);
    if (!digits) return { valid: false, reason: "Phone number is required" };
    if (digits.length < 7) {
      return { valid: false, reason: "Phone number is too short" };
    }
    if (digits.length > 15) {
      return { valid: false, reason: "Phone number is too long" };
    }
    if (!/^\+|^00/.test(rawValue)) {
      return {
        valid: false,
        reason: "Phone number must include a country code",
      };
    }
    var country = getPhoneCountryFromNumber(normalized, getPhoneCountries());
    if (!country) {
      return { valid: false, reason: "Unrecognized country calling code" };
    }
    return {
      valid: true,
      normalized: normalized,
      countryCode: country.code,
    };
  }

  function formatPhoneNumber(value, country) {
    var normalized = normalizePhoneNumber(value);
    if (!normalized) return String(value || "");
    var digits = normalized.slice(1);
    var match;
    if ((country.code === "US" || country.code === "CA") && (match = digits.match(/^1(\d{3})(\d{3})(\d{4})$/))) {
      return "+1 (" + match[1] + ") " + match[2] + "-" + match[3];
    }
    if (country.code === "GB" && (match = digits.match(/^44(\d{4})(\d{6})$/))) {
      return "+44 " + match[1] + " " + match[2];
    }
    if (country.code === "FR" && (match = digits.match(/^33(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/))) {
      return "+33 " + match.slice(1).join(" ");
    }
    if (["IT", "ES", "AU"].indexOf(country.code) >= 0 && (match = digits.slice(country.dialCode.length - 1).match(/^(\d{3})(\d{3})(\d{3,4})$/))) {
      return country.dialCode + " " + match.slice(1).join(" ");
    }
    if (["IN", "JP", "CN", "KR", "BR", "MX", "AE", "SA", "ZA", "NG"].indexOf(country.code) >= 0) {
      var nationalForKnownCountry = digits.slice(country.dialCode.length - 1);
      if (nationalForKnownCountry.length >= 8) {
        return country.dialCode + " " + nationalForKnownCountry.replace(/(\d{3,4})(?=\d)/g, "$1 ").trim();
      }
    }
    var national = digits.slice(country.dialCode.length - 1);
    if (!national) return country.dialCode;
    var groups = [];
    while (national.length > 4) {
      groups.push(national.slice(0, 3));
      national = national.slice(3);
    }
    groups.push(national);
    return country.dialCode + " " + groups.join(" ");
  }

  function formatPhoneFieldValue(value, country) {
    var trimmed = String(value || "").trim();
    if (!trimmed) return "";
    if (!/^\+|^00/.test(trimmed)) {
      var national = trimmed.replace(/\D/g, "").replace(/^0/, "");
      return national ? formatPhoneNumber(country.dialCode + national, country) : "";
    }
    return formatPhoneNumber(trimmed, country);
  }

  function splitPhoneCountryList(value) {
    return String(value || "")
      .split(/[\s,]+/)
      .map(normalizePhoneCountryCode)
      .filter(Boolean);
  }

  function addPhoneFieldClasses(element, classNames) {
    String(classNames || "")
      .split(/\s+/)
      .filter(Boolean)
      .forEach(function (className) {
        element.classList.add(className);
      });
  }

  function getPhoneCountryLabel(country, display) {
    if (display === "flag") return country.code;
    if (display === "code") return country.code;
    if (display === "dial-code") return country.dialCode;
    if (display === "name") return country.name + " (" + country.dialCode + ")";
    return country.code + " " + country.dialCode;
  }

  function getPhoneFieldCountries(options) {
    var allowed = (options.allowedCountries || []).map(normalizePhoneCountryCode);
    var preferred = (options.preferredCountries || []).map(normalizePhoneCountryCode);
    var countries = getPhoneCountries().filter(function (country) {
      return !allowed.length || allowed.indexOf(country.code) >= 0;
    });
    return countries.sort(function (a, b) {
      var aIndex = preferred.indexOf(a.code);
      var bIndex = preferred.indexOf(b.code);
      if (aIndex >= 0 || bIndex >= 0) {
        if (aIndex < 0) return 1;
        if (bIndex < 0) return -1;
        return aIndex - bIndex;
      }
      return a.name.localeCompare(b.name);
    });
  }

  function detectBrowserPhoneCountry() {
    var locale = navigator.language || (navigator.languages && navigator.languages[0]) || "";
    var parts = locale.split(/[-_]/);
    return parts.length > 1 ? normalizePhoneCountryCode(parts[parts.length - 1]) : "US";
  }

  function createPhoneNumberField(target, options) {
    options = options || {};
    var resolved = typeof target === "string" ? document.querySelector(target) : target;
    if (!resolved) throw new Error("Phone number field target not found.");
    if (resolved.__tiquoPhoneFieldController) return resolved.__tiquoPhoneFieldController;

    var input = resolved.tagName === "INPUT"
      ? resolved
      : resolved.querySelector("[data-tiquo-phone-number],input[type='tel'],input");
    if (!input) throw new Error("Phone number field needs an input.");
    if (input.__tiquoPhoneFieldController) return input.__tiquoPhoneFieldController;
    var countries = getPhoneFieldCountries(options);
    if (!countries.length) throw new Error("Phone number field has no allowed countries.");

    var root = resolved === input ? null : resolved;
    var countrySelect = options.countrySelect || (root && root.querySelector("[data-tiquo-phone-country]"));
    var countryControl = root && root.querySelector("[data-tiquo-phone-country-control]");
    var countryFlag = options.countryFlag || (root && root.querySelector("[data-tiquo-phone-flag]"));
    var display = options.countryDisplay || "flag-code";
    var shouldDisplayFlag = display === "flag" || display === "flag-code";
    var generatedRoot = false;
    var generatedSelect = false;
    var generatedCountryControl = false;
    if (!root) {
      root = document.createElement("span");
      root.setAttribute("data-tiquo-phone-field-root", "");
      root.setAttribute("data-tiquo-phone-generated", "true");
      if (!input.parentNode) throw new Error("Phone number input must have a parent.");
      input.parentNode.insertBefore(root, input);
      root.appendChild(input);
      generatedRoot = true;
    }
    if (!countrySelect) {
      countryControl = document.createElement("span");
      countryControl.setAttribute("data-tiquo-phone-country-control", "");
      countryFlag = document.createElement("img");
      countryFlag.setAttribute("data-tiquo-phone-flag", "");
      countryFlag.alt = "";
      countryFlag.setAttribute("aria-hidden", "true");
      countryFlag.loading = "lazy";
      countryFlag.decoding = "async";
      countrySelect = document.createElement("select");
      countrySelect.setAttribute("data-tiquo-phone-country", "");
      countryControl.appendChild(countryFlag);
      countryControl.appendChild(countrySelect);
      root.insertBefore(countryControl, input);
      generatedSelect = true;
      generatedCountryControl = true;
    }

    root.classList.add("tiquo-phone-field");
    if (countryControl) countryControl.classList.add("tiquo-phone-field__country-control");
    countrySelect.classList.add("tiquo-phone-field__country");
    if (countryFlag) countryFlag.classList.add("tiquo-phone-field__flag");
    input.classList.add("tiquo-phone-field__input");
    addPhoneFieldClasses(root, options.rootClass);
    if (countryControl) addPhoneFieldClasses(countryControl, options.countryControlClass);
    addPhoneFieldClasses(countrySelect, options.countryClass);
    if (countryFlag) addPhoneFieldClasses(countryFlag, options.flagClass);
    addPhoneFieldClasses(input, options.inputClass);
    if (countryControl) {
      countryControl.setAttribute(
        "data-tiquo-phone-has-flag",
        String(!!countryFlag && shouldDisplayFlag),
      );
    }
    if (countryFlag) countryFlag.hidden = !shouldDisplayFlag;

    if (!input.getAttribute("type")) input.type = "tel";
    if (!input.getAttribute("inputmode")) input.setAttribute("inputmode", "tel");
    if (!input.getAttribute("autocomplete")) input.setAttribute("autocomplete", "tel");

    if (options.populateCountrySelect !== false) {
      while (countrySelect.firstChild) countrySelect.removeChild(countrySelect.firstChild);
      countries.forEach(function (country) {
        var option = document.createElement("option");
        option.value = country.code;
        option.textContent = getPhoneCountryLabel(country, display);
        option.title = country.name + " (" + country.dialCode + ")";
        option.setAttribute("aria-label", country.name + " (" + country.dialCode + ")");
        option.setAttribute("data-tiquo-phone-option", "");
        countrySelect.appendChild(option);
      });
    }
    if (!countrySelect.getAttribute("aria-label")) {
      countrySelect.setAttribute(
        "aria-label",
        "Country code for " + (input.getAttribute("aria-label") || input.name || "phone number"),
      );
    }

    var detected = /^\+|^00/.test(String(input.value || "").trim())
      ? getPhoneCountryFromNumber(input.value, countries)
      : null;
    var initialCodes = [
      detected && detected.code,
      options.defaultCountry,
      detectBrowserPhoneCountry(),
      "US",
    ].map(normalizePhoneCountryCode);
    var initialCountry = null;
    for (var i = 0; i < initialCodes.length; i += 1) {
      initialCountry = getPhoneCountry(initialCodes[i], countries);
      if (initialCountry) break;
    }
    initialCountry = initialCountry || countries[0];
    var selectedCountryCode = initialCountry.code;
    countrySelect.value = selectedCountryCode;

    function currentCountry() {
      return getPhoneCountry(selectedCountryCode, countries) || countries[0];
    }

    function syncPhoneCountryPresentation(country) {
      countrySelect.title = country.name + " (" + country.dialCode + ")";
      if (!countryFlag || !shouldDisplayFlag) return;
      countryFlag.hidden = false;
      countryFlag.src = getPhoneCountryFlagUrl(country.code, options.flagBaseUrl);
      countryFlag.setAttribute("data-tiquo-phone-country-code", country.code);
      countryFlag.title = country.name;
    }

    function readPhoneFieldValue() {
      var country = currentCountry();
      var normalized = normalizePhoneNumber(input.value);
      var empty = !String(input.value || "").trim() || normalized === country.dialCode;
      if (empty) {
        return input.required
          ? { value: input.value, e164: "", countryCode: country.code, valid: false, reason: "Phone number is required" }
          : { value: input.value, e164: "", countryCode: country.code, valid: true };
      }
      var validation = validatePhoneNumber(input.value);
      var detectedCountry = validation.countryCode
        ? getPhoneCountry(validation.countryCode, getPhoneCountries())
        : null;
      if (validation.valid && detectedCountry && detectedCountry.dialCode !== country.dialCode) {
        return {
          value: input.value,
          e164: "",
          countryCode: country.code,
          valid: false,
          reason: "Phone number does not match the selected country",
        };
      }
      return {
        value: input.value,
        e164: validation.valid ? validation.normalized : "",
        countryCode: country.code,
        valid: validation.valid,
        reason: validation.reason,
      };
    }

    function setPhoneFieldValidity(change, expose) {
      if (options.validate !== false && input.setCustomValidity) {
        input.setCustomValidity(change.valid ? "" : change.reason || "Invalid phone number");
      }
      root.setAttribute(
        "data-tiquo-phone-valid",
        change.e164 || String(input.value || "").trim() ? String(change.valid) : "empty",
      );
      if (expose && !change.valid) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
    }

    function emitPhoneFieldChange(change) {
      if (typeof options.onChange === "function") options.onChange(change);
      if (typeof window.CustomEvent === "function") {
        input.dispatchEvent(new CustomEvent("tiquo:phone-change", {
          bubbles: true,
          detail: change,
        }));
      }
    }

    function syncPhoneField(expose, shouldEmit) {
      var detectedCountry = /^\+|^00/.test(String(input.value || "").trim())
        ? getPhoneCountryFromNumber(input.value, countries)
        : null;
      if (detectedCountry) {
        selectedCountryCode = detectedCountry.code;
        countrySelect.value = selectedCountryCode;
        syncPhoneCountryPresentation(currentCountry());
      }
      var trimmedValue = String(input.value || "").trim();
      if (trimmedValue && !/^\+|^00/.test(trimmedValue)) {
        var nationalDigits = trimmedValue.replace(/\D/g, "").replace(/^0/, "");
        if (nationalDigits) {
          var internationalValue = currentCountry().dialCode + nationalDigits;
          input.value = options.autoFormat === false
            ? internationalValue
            : formatPhoneNumber(internationalValue, currentCountry());
        }
      } else if (options.autoFormat !== false && trimmedValue) {
        var numberCountry = getPhoneCountryFromNumber(trimmedValue, getPhoneCountries());
        input.value = formatPhoneFieldValue(
          input.value,
          numberCountry || currentCountry(),
        );
      }
      var change = readPhoneFieldValue();
      setPhoneFieldValidity(change, expose);
      if (shouldEmit) emitPhoneFieldChange(change);
      return change;
    }

    function setPhoneFieldCountry(countryCode, shouldEmit) {
      var nextCountry = getPhoneCountry(countryCode, countries);
      if (!nextCountry) throw new Error("Unsupported phone country: " + countryCode);
      var previousCountry = currentCountry();
      var normalized = normalizePhoneNumber(input.value);
      var national;
      if (normalized.indexOf(previousCountry.dialCode) === 0) {
        national = normalized.slice(previousCountry.dialCode.length);
      } else {
        var detectedPreviousCountry = getPhoneCountryFromNumber(
          normalized,
          getPhoneCountries(),
        );
        national = detectedPreviousCountry && normalized.indexOf(detectedPreviousCountry.dialCode) === 0
          ? normalized.slice(detectedPreviousCountry.dialCode.length)
          : String(input.value || "").replace(/\D/g, "").replace(/^0/, "");
      }
      selectedCountryCode = nextCountry.code;
      countrySelect.value = selectedCountryCode;
      input.value = national
        ? formatPhoneNumber(nextCountry.dialCode + national, nextCountry)
        : "";
      syncPhoneCountryPresentation(nextCountry);
      var change = readPhoneFieldValue();
      setPhoneFieldValidity(change, false);
      if (shouldEmit !== false) emitPhoneFieldChange(change);
      return change;
    }

    function handlePhoneInput() {
      syncPhoneField(false, true);
    }
    function handlePhoneBlur() {
      syncPhoneField(true, true);
    }
    function handlePhoneCountryChange() {
      setPhoneFieldCountry(countrySelect.value, true);
    }
    function handlePhoneFormSubmit() {
      var change = syncPhoneField(true, false);
      if (change.e164) input.value = change.e164;
    }

    input.addEventListener("input", handlePhoneInput);
    input.addEventListener("blur", handlePhoneBlur);
    countrySelect.addEventListener("change", handlePhoneCountryChange);
    if (input.form) input.form.addEventListener("submit", handlePhoneFormSubmit);

    syncPhoneCountryPresentation(currentCountry());
    if (String(input.value || "").trim()) syncPhoneField(false, false);
    else setPhoneFieldCountry(initialCountry.code, false);

    var controller = {
      input: input,
      countrySelect: countrySelect,
      countryFlag: countryFlag,
      root: root,
      getValue: readPhoneFieldValue,
      setValue: function (value) {
        input.value = value;
        return syncPhoneField(false, true);
      },
      setCountry: function (countryCode) {
        return setPhoneFieldCountry(countryCode, true);
      },
      destroy: function () {
        input.removeEventListener("input", handlePhoneInput);
        input.removeEventListener("blur", handlePhoneBlur);
        countrySelect.removeEventListener("change", handlePhoneCountryChange);
        if (input.form) input.form.removeEventListener("submit", handlePhoneFormSubmit);
        if (input.setCustomValidity) input.setCustomValidity("");
        input.removeAttribute("aria-invalid");
        input.__tiquoPhoneFieldController = null;
        resolved.__tiquoPhoneFieldController = null;
        if (generatedCountryControl && countryControl && countryControl.parentNode) {
          countryControl.parentNode.removeChild(countryControl);
        } else if (generatedSelect && countrySelect.parentNode) {
          countrySelect.parentNode.removeChild(countrySelect);
        }
        if (generatedRoot && root.parentNode) {
          root.parentNode.insertBefore(input, root);
          root.parentNode.removeChild(root);
        }
      },
    };
    input.__tiquoPhoneFieldController = controller;
    resolved.__tiquoPhoneFieldController = controller;
    return controller;
  }

  function bindPhoneNumberFields() {
    selectAll("[data-tiquo-phone-field]").forEach(function (element) {
      if (element.__tiquoPhoneFieldController) return;
      try {
        createPhoneNumberField(element, {
          defaultCountry: element.getAttribute("data-tiquo-default-country") || undefined,
          preferredCountries: splitPhoneCountryList(element.getAttribute("data-tiquo-preferred-countries")),
          allowedCountries: splitPhoneCountryList(element.getAttribute("data-tiquo-allowed-countries")),
          countryDisplay: element.getAttribute("data-tiquo-country-display") || "flag-code",
          flagBaseUrl: element.getAttribute("data-tiquo-flag-base-url") || DEFAULT_PHONE_FLAG_BASE_URL,
          rootClass: element.getAttribute("data-tiquo-root-class") || "",
          countryControlClass: element.getAttribute("data-tiquo-country-control-class") || "",
          countryClass: element.getAttribute("data-tiquo-country-class") || "",
          flagClass: element.getAttribute("data-tiquo-flag-class") || "",
          inputClass: element.getAttribute("data-tiquo-input-class") || "",
          populateCountrySelect: readBoolean(element.getAttribute("data-tiquo-populate-countries"), true),
          autoFormat: readBoolean(element.getAttribute("data-tiquo-auto-format"), true),
          validate: readBoolean(element.getAttribute("data-tiquo-validate"), true),
        });
      } catch (error) {
        log("Phone number field could not be initialized", error);
      }
    });
  }

  function observePhoneNumberFields() {
    if (!window.MutationObserver || phoneFieldObserver) return;
    phoneFieldObserver = new MutationObserver(function () {
      bindPhoneNumberFields();
    });
    phoneFieldObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  // full: analytics linked to the signed-in customer. anonymous: analytics
  // without the customer's identity. cookieless: as anonymous, but nothing is
  // stored in the browser and Tiquo groups events into sessions on the server.
  // none: no analytics events.
  function normalizeConsentMode(value) {
    var mode = String(value || "").trim().toLowerCase();
    if (mode === "full" || mode === "anonymous" || mode === "cookieless" || mode === "none") {
      return mode;
    }
    if (mode === "true" || mode === "yes" || mode === "on") return "full";
    if (mode === "false" || mode === "no" || mode === "off") return "none";
    return "anonymous";
  }

  function canTrackAnalytics() {
    return analyticsEnabled && consentMode !== "none";
  }

  function canAttachIdentity() {
    return canTrackAnalytics() && consentMode === "full";
  }

  function setConsent(mode) {
    consentMode = normalizeConsentMode(mode);
    safeStorage(window.localStorage, consentStorageKey, consentMode);
    if (consentMode === "cookieless" || consentMode === "none") {
      forgetClientSessionId();
    }

    if (canTrackAnalytics() && !analyticsInitialized) {
      initAnalytics();
    }

    if (canAttachIdentity() && session && session.customer) {
      identify();
    }

    return consentMode;
  }

  function isLocalDevelopmentHost() {
    var hostname = window.location.hostname;
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
  }

  function normalizeApiEndpoint(value) {
    try {
      var url = new URL(String(value));
      return url.origin;
    } catch {
      return DEFAULT_API_ENDPOINT;
    }
  }

  function log() {
    if (!debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[Tiquo]");
    window.console.log.apply(window.console, args);
  }

  function printBranding(force) {
    if (!window.console) return;
    if (!force && window.__tiquoHostedBrandingPrinted) return;

    try {
      window.__tiquoHostedBrandingPrinted = true;
      var art = [
        " _   _                   ",
        "| |_(_) __ _ _   _  ___  ",
        "| __| |/ _` | | | |/ _ \\ ",
        "| |_| | (_| | |_| | (_) |",
        " \\__|_|\\__, |\\__,_|\\___/ ",
        "          |_|            ",
      ].join("\n");

      window.console.info(
        art + "\nTiquo Hosted Package v" + VERSION + " loaded\nhttps://tiquo.co",
      );
    } catch {
      // Ignore environments where console styling is unavailable.
    }
  }

  function installVisibilityStyles() {
    if (document.getElementById("tiquo-hosted-visibility-style")) return;
    var style = document.createElement("style");
    style.id = "tiquo-hosted-visibility-style";
    style.textContent =
      "html:not([data-tiquo-auth-ready='true']) [data-tiquo-show],html:not([data-tiquo-auth-ready='true']) [data-tiquo-customer],html:not([data-tiquo-auth-ready='true']) [data-tiquo-form='passwordless-login'],html:not([data-tiquo-auth-ready='true']) [data-tiquo-form='passwordless-signup']{visibility:hidden!important;}" +
      "[data-tiquo-show][hidden],[data-tiquo-passwordless][hidden],[data-tiquo-rendered-template],[data-tiquo-empty][hidden]{display:none!important;}" +
      ".tiquo-form-message{margin:12px 0 0;font:inherit;line-height:1.4;color:inherit;opacity:.8;}";
    (document.head || document.documentElement).appendChild(style);
  }

  function installPhoneNumberFieldStyles() {
    if (document.getElementById("tiquo-hosted-phone-field-style")) return;
    var style = document.createElement("style");
    style.id = "tiquo-hosted-phone-field-style";
    style.textContent =
      ".tiquo-phone-field[data-tiquo-phone-generated='true']{display:flex;align-items:stretch;width:100%;max-width:100%;}" +
      ".tiquo-phone-field[data-tiquo-phone-generated='true']>.tiquo-phone-field__country-control{box-sizing:border-box;flex:0 0 var(--tiquo-phone-country-width,8.5rem);width:var(--tiquo-phone-country-width,8.5rem);min-height:var(--tiquo-phone-min-height,40px);position:relative;}" +
      ".tiquo-phone-field[data-tiquo-phone-generated='true'] .tiquo-phone-field__country{box-sizing:border-box;width:100%;height:100%;min-height:var(--tiquo-phone-min-height,40px);padding:0 var(--tiquo-phone-country-padding,.65rem);font:inherit;color:inherit;background:var(--tiquo-phone-background,Canvas);border:var(--tiquo-phone-border,1px solid #d1d5db);border-radius:var(--tiquo-phone-radius,.375rem) 0 0 var(--tiquo-phone-radius,.375rem);cursor:pointer;position:relative;z-index:1;}" +
      ".tiquo-phone-field[data-tiquo-phone-generated='true'] .tiquo-phone-field__country-control[data-tiquo-phone-has-flag='true']>.tiquo-phone-field__country{padding-left:calc(var(--tiquo-phone-country-padding,.65rem) + var(--tiquo-phone-flag-size,1.25rem) + var(--tiquo-phone-flag-gap,.5rem));}" +
      ".tiquo-phone-field__flag{box-sizing:border-box;width:var(--tiquo-phone-flag-size,1.25rem);height:var(--tiquo-phone-flag-size,1.25rem);border-radius:50%;object-fit:cover;outline:1px solid rgba(0,0,0,.1);outline-offset:-1px;}" +
      ".tiquo-phone-field[data-tiquo-phone-generated='true'] .tiquo-phone-field__flag{position:absolute;left:var(--tiquo-phone-country-padding,.65rem);top:50%;transform:translateY(-50%);pointer-events:none;z-index:2;}" +
      ".tiquo-phone-field[data-tiquo-phone-generated='true']>.tiquo-phone-field__input{box-sizing:border-box;flex:1 1 auto;min-width:0;border-top-left-radius:0;border-bottom-left-radius:0;}" +
      ".tiquo-phone-field[data-tiquo-phone-generated='true'] .tiquo-phone-field__country:focus{z-index:3;}";
    (document.head || document.documentElement).appendChild(style);
  }

  function setAuthReady(readyState) {
    authReady = !!readyState;
    if (!document.documentElement || !document.documentElement.setAttribute) return;
    document.documentElement.setAttribute(
      "data-tiquo-auth-ready",
      authReady ? "true" : "false",
    );
  }

  function onReady(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  }

  function ready(callback) {
    if (typeof callback !== "function") return;
    readyCallbacks.push(callback);
    if (document.readyState !== "loading") {
      setTimeout(function () {
        callback(Tiquo);
      }, 0);
    }
  }

  function runReadyCallbacks() {
    var callbacks = readyCallbacks.slice();
    readyCallbacks = [];
    callbacks.forEach(function (callback) {
      try {
        callback(Tiquo);
      } catch (error) {
        log("Ready callback failed", error);
      }
    });
  }

  function request(path, options) {
    var requestOptions = options || {};
    requestOptions.headers = requestOptions.headers || {};
    if (!requestOptions.headers["Content-Type"] && requestOptions.body) {
      requestOptions.headers["Content-Type"] = "text/plain;charset=UTF-8";
    }
    return fetch(apiEndpoint + path, requestOptions);
  }

  function jsonRequest(path, body, options) {
    var requestOptions = options || {};
    requestOptions.method = requestOptions.method || "POST";
    requestOptions.headers = requestOptions.headers || {};
    requestOptions.headers["Content-Type"] = "application/json";
    requestOptions.body = JSON.stringify(body || {});
    return request(path, requestOptions);
  }

  function randomId() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return (
      Date.now().toString(36) +
      "-" +
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2)
    );
  }

  function safeStorage(storage, key, value) {
    try {
      if (arguments.length === 2) return storage.getItem(key);
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, value);
    } catch {
      return null;
    }
  }

  // The session ID is kept in sessionStorage, so it is only created once an
  // event is sent with consent that allows storage. Without one (cookieless),
  // Tiquo groups the visitor's events into sessions on the server.
  function sessionIdFor(mode) {
    if (mode === "cookieless" || mode === "none") return undefined;
    return getClientSessionId();
  }

  function forgetClientSessionId() {
    safeStorage(window.sessionStorage, analyticsSessionKey(), null);
  }

  function analyticsSessionKey() {
    return "tiquo_analytics_session_" + publicKey;
  }

  function getClientSessionId() {
    var key = analyticsSessionKey();
    var now = Date.now();
    var stored = safeStorage(window.sessionStorage, key);
    if (stored) {
      try {
        var existing = JSON.parse(stored);
        if (
          existing &&
          typeof existing.id === "string" &&
          existing.id &&
          typeof existing.lastActivityAt === "number" &&
          now >= existing.lastActivityAt - 60000 &&
          now - existing.lastActivityAt <= ANALYTICS_SESSION_TIMEOUT_MS
        ) {
          existing.lastActivityAt = now;
          safeStorage(window.sessionStorage, key, JSON.stringify(existing));
          return existing.id;
        }
      } catch {
        // Legacy values contained only the ID. Rotate once into the expiring
        // session record so an old browser tab cannot remain open forever.
      }
    }
    var next = randomId();
    safeStorage(window.sessionStorage, key, JSON.stringify({
      id: next,
      lastActivityAt: now,
    }));
    return next;
  }

  function decodeJwt(token) {
    try {
      var payload = token.split(".")[1];
      var normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
      var decoded = window.atob(normalized);
      return JSON.parse(decodeURIComponent(escape(decoded)));
    } catch {
      return null;
    }
  }

  function normalizeHostname(hostname) {
    return String(hostname || "")
      .trim()
      .toLowerCase()
      .replace(/\.$/, "")
      .replace(/^www\./, "");
  }

  function getUtmParams(search) {
    var params = new URLSearchParams(search);
    return {
      source: params.get("utm_source") || undefined,
      medium: params.get("utm_medium") || undefined,
      campaign: params.get("utm_campaign") || undefined,
      content: params.get("utm_content") || undefined,
      term: params.get("utm_term") || undefined,
    };
  }

  function getBrowser() {
    var ua = navigator.userAgent || "";
    if (/Edg\//.test(ua)) return "Edge";
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
    if (/Firefox\//.test(ua)) return "Firefox";
    return "Unknown";
  }

  function getDevice() {
    var ua = navigator.userAgent || "";
    if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
    if (/iPad|Tablet/i.test(ua)) return "tablet";
    return "desktop";
  }

  function getOperatingSystem() {
    var ua = navigator.userAgent || "";
    if (/Windows/i.test(ua)) return "Windows";
    if (/Mac OS|Macintosh/i.test(ua)) return "macOS";
    if (/Android/i.test(ua)) return "Android";
    if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
    if (/Linux/i.test(ua)) return "Linux";
    return "Unknown";
  }

  function initAnalytics() {
    if (analyticsInitialized || !canTrackAnalytics()) return;
    analyticsInitialized = true;
    patchHistory();
    trackPageview();
    window.addEventListener(ANALYTICS_LOCATION_EVENT, handleLocationChange);
    // pagehide and visibilitychange both fire on a normal unload; the
    // engagementSent flag keeps that to one engagement event.
    window.addEventListener("pagehide", handlePageHidden);
    window.addEventListener("pageshow", function (event) {
      if (event && event.persisted) engagementSent = false;
    });
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") handlePageHidden();
      else engagementSent = false;
    });
  }

  function patchHistory() {
    if (window.__tiquoHistoryPatched) return;
    window.__tiquoHistoryPatched = true;
    ["pushState", "replaceState"].forEach(function (method) {
      var original = history[method];
      history[method] = function () {
        var result = original.apply(this, arguments);
        window.dispatchEvent(new Event(ANALYTICS_LOCATION_EVENT));
        return result;
      };
    });
    window.addEventListener("popstate", function () {
      window.dispatchEvent(new Event(ANALYTICS_LOCATION_EVENT));
    });
  }

  function trackPageview(options) {
    options = options || {};
    var path = options.path || window.location.pathname + window.location.search;
    if (path === lastPath) return Promise.resolve();
    beginPageview(path);
    return sendPageview(
      extend({}, options, { path: path }),
      window.location.search,
    );
  }

  // Client-side navigation. The path, URL and time are taken now; the title
  // is read once the router has set it (see PAGEVIEW_TITLE_DELAY_MS).
  function handleLocationChange() {
    var path = window.location.pathname + window.location.search;
    if (path === lastPath) return;
    var url = window.location.href;
    var search = window.location.search;
    var timestamp = Date.now();
    beginPageview(path);

    var timer = setTimeout(send, PAGEVIEW_TITLE_DELAY_MS);
    function send() {
      if (pendingPageview !== send) return;
      pendingPageview = null;
      clearTimeout(timer);
      sendPageview({ path: path, url: url }, search, timestamp);
    }
    pendingPageview = send;
  }

  function handlePageHidden() {
    flushPendingPageview();
    trackEngagement();
  }

  function flushPendingPageview() {
    if (pendingPageview) pendingPageview();
  }

  // Closes out the previous page (pageview, engagement) and starts `path`.
  function beginPageview(path) {
    flushPendingPageview();
    if (lastPath) trackEngagement();
    lastPath = path;
    pageStartedAt = Date.now();
    engagementSent = false;
  }

  function sendPageview(options, search, timestamp) {
    return trackAt("pageview", extend({}, options, {
      url: options.url || window.location.href,
      title: options.title || document.title,
      referrer: options.referrer || document.referrer || undefined,
    }), search, timestamp);
  }

  // At most one engagement per pageview per hide cycle (see engagementSent).
  function trackEngagement() {
    if (!lastPath || engagementSent) return Promise.resolve();
    engagementSent = true;
    var scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body ? document.body.scrollHeight : 0,
      window.innerHeight,
    );
    var scrollDepth = scrollHeight
      ? Math.min(1, (window.scrollY + window.innerHeight) / scrollHeight)
      : undefined;
    return track("engagement", {
      path: lastPath,
      durationMs: Date.now() - pageStartedAt,
      scrollDepth: scrollDepth,
    });
  }

  function track(eventType, options) {
    return trackAt(eventType, options, window.location.search);
  }

  // `search` is the query string UTM parameters are read from; `timestamp` is
  // when the event happened, for events sent after a delay.
  function trackAt(eventType, options, search, timestamp) {
    if (!canTrackAnalytics()) {
      return Promise.resolve({ success: true, skipped: true, reason: "consent_none" });
    }

    options = options || {};
    var utm = getUtmParams(search);
    var payload = extend({
      publicKey: publicKey,
      eventId: randomId(),
      eventType: eventType,
      eventName: options.eventName,
      eventProperties: options.eventProperties,
      clientSessionId: sessionIdFor(consentMode),
      siteSlug: options.siteSlug,
      pageSlug: options.pageSlug,
      hostname: normalizeHostname(window.location.hostname),
      path: options.path || window.location.pathname + window.location.search,
      url: options.url || window.location.href,
      title: options.title || document.title,
      referrer: options.referrer || document.referrer || undefined,
      timestamp: timestamp || Date.now(),
      browser: getBrowser(),
      device: getDevice(),
      operatingSystem: getOperatingSystem(),
      language: navigator.language,
      screenWidth: window.screen && window.screen.width,
      screenHeight: window.screen && window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      durationMs: options.durationMs,
      scrollDepth: options.scrollDepth,
      identityLinkType: canAttachIdentity() ? options.identityLinkType : undefined,
    }, utm);

    var headers = {
      "Content-Type": "text/plain;charset=UTF-8",
    };
    if (accessToken && canAttachIdentity()) headers.Authorization = "Bearer " + accessToken;

    var analyticsUrl = apiEndpoint + "/api/dom-analytics/event?publicKey=" +
      encodeURIComponent(publicKey);

    return fetch(analyticsUrl, {
      method: "POST",
      headers: headers,
      credentials: "include",
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(function (error) {
      log("Analytics event failed", error);
    });
  }

  function identify(options) {
    if (!canAttachIdentity()) {
      return Promise.resolve({ success: true, skipped: true, reason: "consent_not_full" });
    }
    return track("identify", extend({ identityLinkType: "auth_dom_login" }, options || {}));
  }

  function sendOTP(email) {
    return jsonRequest("/api/auth-dom/otp/send", {
      publicKey: publicKey,
      email: email,
    }).then(expectOk("Failed to send OTP"));
  }

  function verifyOTP(email, otp) {
    return jsonRequest("/api/auth-dom/otp/verify", {
      publicKey: publicKey,
      email: email,
      otp: otp,
    })
      .then(expectOk("Invalid verification code"))
      .then(function (result) {
        accessToken = result.accessToken;
        refreshToken = result.refreshToken;
        saveTokens();
        return refreshSession().then(function () {
          identify();
          notifyAuthState();
          return result;
        });
      });
  }

  function getUser() {
    if (!accessToken && !refreshToken) return Promise.resolve(null);
    return ensureValidToken().then(refreshSession);
  }

  function updateProfile(updates) {
    return ensureValidToken().then(function () {
      if (!accessToken) throw new Error("Not authenticated");
      var body = extend({}, updates || {});
      delete body.displayName;
      delete body.createdAt;
      delete body.firstActiveAt;
      var profilePhotoUpload = extractUploadableProfilePhoto(body.profilePhoto);
      if (profilePhotoUpload) delete body.profilePhoto;
      if (typeof body.phone === "string" && body.phone) {
        body.phone = /^\s*(?:\+|00)/.test(body.phone)
          ? normalizePhoneNumber(body.phone)
          : body.phone.replace(/\D/g, "");
      }
      if (Array.isArray(body.phones)) {
        body.phones = body.phones.map(function (phone) {
          if (!phone || typeof phone.number !== "string" || !phone.number) return phone;
          return extend({}, phone, {
            number: /^\s*(?:\+|00)/.test(phone.number)
              ? normalizePhoneNumber(phone.number)
              : phone.number.replace(/\D/g, ""),
          });
        });
      }

      var profileUpdate = Object.keys(body).length > 0
        ? fetch(apiEndpoint + "/api/client/v1/profile", {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
            credentials: "include",
            body: JSON.stringify(body),
          }).then(expectOk("Failed to update profile"))
        : Promise.resolve({
            success: true,
            data: { customer: session && session.customer },
          });

      return profileUpdate
        .then(function (result) {
          var customer = result.data && result.data.customer ? result.data.customer : result.customer;
          if (session && customer) {
            session = extend({}, session, { customer: customer });
            notifyAuthState();
            renderAttributes();
          }
          return profilePhotoUpload ? uploadProfilePhoto(profilePhotoUpload) : result;
        });
    });
  }

  function subscribeToMarketing(input) {
    if (!input || typeof input.email !== "string" || !input.email.trim()) {
      return Promise.reject(new Error("email is required"));
    }
    return fetch(apiEndpoint + "/api/client/v1/marketing/subscribe", {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json", "X-Public-Key": publicKey },
      body: JSON.stringify(extend({}, input, { publicKey: publicKey })),
    }).then(expectOk("Failed to subscribe to marketing")).then(function () {
      return { success: true };
    });
  }

  function createEnquiry(input) {
    return ensureValidToken().then(function () {
      var customer = extend({}, input && input.customer || {});
      var enquiry = extend({}, input && input.enquiry || {});
      if (!String(customer.email || "").trim()) throw new Error("customer.email is required");
      if (!String(enquiry.subject || "").trim() && !String(enquiry.message || "").trim()) {
        throw new Error("enquiry.message or enquiry.subject is required");
      }
      if (typeof customer.phone === "string" && customer.phone) {
        customer.phone = /^\s*(?:\+|00)/.test(customer.phone)
          ? normalizePhoneNumber(customer.phone) : customer.phone.replace(/\D/g, "");
      }
      var headers = { "Content-Type": "application/json", "X-Public-Key": publicKey };
      if (accessToken) headers.Authorization = "Bearer " + accessToken;
      return fetch(apiEndpoint + "/api/client/v1/enquiries", {
        method: "POST", headers: headers, credentials: "include",
        body: JSON.stringify({ publicKey: publicKey, customer: customer, enquiry: enquiry }),
      }).then(expectOk("Failed to create enquiry")).then(function (result) {
        var data = result.data || result;
        if (session && data.customer) {
          session = extend({}, session, { customer: data.customer });
          notifyAuthState();
          renderAttributes();
        }
        return extend({ success: true }, data);
      });
    });
  }

  function uploadProfilePhoto(photo) {
    return ensureValidToken()
      .then(function () {
        if (!accessToken) throw new Error("Not authenticated");
        return profilePhotoToBlob(photo);
      })
      .then(function (blob) {
        if (["image/jpeg", "image/png", "image/gif", "image/webp"].indexOf(blob.type.toLowerCase()) === -1) {
          throw new Error("Profile photo must be a JPEG, PNG, GIF, or WebP image.");
        }
        return fetch(apiEndpoint + "/api/client/v1/profile/photo-upload-url", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
          credentials: "include",
          body: JSON.stringify({}),
        })
          .then(expectOk("Failed to create profile photo upload URL"))
          .then(function (result) {
            var uploadUrl = result.data && result.data.uploadUrl
              ? result.data.uploadUrl
              : result.uploadUrl;
            if (!uploadUrl) throw new Error("Profile photo upload URL was missing.");
            return fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": blob.type },
              body: blob,
            });
          })
          .then(expectOk("Failed to upload profile photo"))
          .then(function (result) {
            var storageId = result.storageId || (result.data && result.data.storageId);
            if (!storageId) throw new Error("Profile photo storage ID was missing.");
            return fetch(apiEndpoint + "/api/client/v1/profile/photo", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + accessToken,
              },
              credentials: "include",
              body: JSON.stringify({ storageId: storageId }),
            });
          })
          .then(expectOk("Failed to update profile photo"))
          .then(function (result) {
            var customer = result.data && result.data.customer ? result.data.customer : result.customer;
            var profilePhoto = result.data && result.data.profilePhoto
              ? result.data.profilePhoto
              : result.profilePhoto;
            if (!customer || !profilePhoto) {
              throw new Error("Profile photo update response was incomplete.");
            }
            if (session) {
              session = extend({}, session, { customer: customer });
              notifyAuthState();
              renderAttributes();
            }
            return extend({}, result, {
              success: true,
              customer: customer,
              profilePhoto: profilePhoto,
            });
          });
      });
  }

  function extractUploadableProfilePhoto(profilePhoto) {
    if (typeof Blob !== "undefined" && profilePhoto instanceof Blob) return profilePhoto;
    if (typeof profilePhoto !== "string") return null;
    var value = profilePhoto.trim();
    return value.startsWith("data:") || value.startsWith("blob:") ? value : null;
  }

  function profilePhotoToBlob(photo) {
    if (typeof Blob !== "undefined" && photo instanceof Blob) {
      return Promise.resolve(photo);
    }
    if (typeof photo === "string") {
      var value = photo.trim();
      if (value.startsWith("data:") || value.startsWith("blob:")) {
        return fetch(value).then(function (response) {
          if (!response.ok) throw new Error("Failed to read profile photo.");
          return response.blob();
        });
      }
    }
    return Promise.reject(new Error(
      "Profile photo must be a File, Blob, data URI, or blob URI.",
    ));
  }

  function isAuthenticated() {
    return !!(session || accessToken || refreshToken);
  }

  function logout() {
    var token = refreshToken;
    walletCardLinksCache = {};
    clearTokens();
    notifyAuthState();
    renderAttributes();
    if (!token) return Promise.resolve({ success: true });
    return jsonRequest("/api/auth-dom/logout", {
      publicKey: publicKey,
      refreshToken: token,
    }).catch(function () {
      return { success: true };
    });
  }

  function refreshSession() {
    return ensureSessionAccessToken().then(function (hasAccessToken) {
      if (!hasAccessToken) {
        session = null;
        notifyAuthState();
        renderAttributes();
        return null;
      }
      return requestSessionPayload(false).then(applySessionPayload);
    });
  }

  function ensureSessionAccessToken() {
    if (!accessToken && refreshToken) return refreshTokenIfNeeded();
    if (!accessToken) return Promise.resolve(false);

    var payload = decodeJwt(accessToken);
    var expiresSoon = payload && payload.exp
      ? payload.exp * 1000 - Date.now() <= 60000
      : false;
    if (expiresSoon && refreshToken) return refreshTokenIfNeeded();
    return Promise.resolve(true);
  }

  function requestSessionPayload(hasRetried) {
    return fetch(apiEndpoint + "/api/client/v1/profile", {
      method: "GET",
      headers: { Authorization: "Bearer " + accessToken },
      credentials: "include",
    })
      .then(function (response) {
        if (response.ok) return response.json();

        if (
          !hasRetried &&
          refreshToken &&
          (response.status === 401 || response.status === 403)
        ) {
          return refreshTokenIfNeeded().then(function (refreshed) {
            if (refreshed) return requestSessionPayload(true);
            return null;
          });
        }

        if (response.status === 401 || response.status === 403) {
          clearTokens();
          return null;
        }

        log("Session refresh request failed with status", response.status);
        return { __tiquoSessionUnavailable: true };
      })
      .catch(function (error) {
        log("Session refresh failed", error);
        return { __tiquoSessionUnavailable: true };
      });
  }

  function applySessionPayload(result) {
    if (result && result.__tiquoSessionUnavailable) return session;
    if (result && result.success && result.data) {
      var payload = decodeJwt(accessToken);
      session = {
        user: result.data.user,
        customer: result.data.customer,
        expiresAt: payload && payload.exp ? payload.exp * 1000 : Date.now() + 3600000,
      };
      scheduleRefresh();
      notifyAuthState();
      renderAttributes();
      if (session.customer) identify();
      return session;
    }
    session = null;
    notifyAuthState();
    renderAttributes();
    return null;
  }

  function ensureValidToken() {
    if (!accessToken) return refreshTokenIfNeeded();
    var payload = decodeJwt(accessToken);
    if (!payload || !payload.exp) return Promise.resolve(true);
    if (payload.exp * 1000 - Date.now() > 60000) return Promise.resolve(true);
    return refreshTokenIfNeeded();
  }

  function getIframeToken(customerFlowId, handoffVersion) {
    return ensureValidToken().then(function () {
      if (!accessToken) throw new Error("Not authenticated");
      return jsonRequest("/api/auth-dom/iframe-token", {
        publicKey: publicKey,
        sessionToken: accessToken,
        customerFlowId: customerFlowId,
        handoffVersion: handoffVersion === 1 ? 1 : undefined,
      }).then(expectOk("Failed to generate iframe token"));
    });
  }

  function parseTiquoEmbedTarget(src) {
    try {
      var url = new URL(src, window.location.origin);
      // A local Tiquo dev server is trusted only from a page that is itself
      // local, so a live site never hands a customer token to localhost.
      var isTiquoHost =
        url.hostname === "book.tiquo.app" ||
        url.hostname.slice(-10) === ".tiquo.app" ||
        (url.hostname === "localhost" && isLocalDevelopmentHost());
      if (!isTiquoHost || url.pathname.indexOf("/embed/") !== 0) return null;

      var segments = url.pathname.split("/").filter(Boolean);
      var flowId =
        segments[1] === "membership" ||
        segments[1] === "guest-pre-registration"
          ? segments[2]
          : segments[1];
      return {
        origin: url.origin,
        flowId: flowId || undefined,
      };
    } catch {
      return null;
    }
  }

  function isSafeIframeNonce(value) {
    return (
      typeof value === "string" &&
      value.length >= 16 &&
      value.length <= 128 &&
      /^[A-Za-z0-9_-]+$/.test(value)
    );
  }

  function findIframeBridge(iframe) {
    for (var index = 0; index < iframeBridges.length; index += 1) {
      if (iframeBridges[index].iframe === iframe) return iframeBridges[index];
    }
    return null;
  }

  function registerIframeAuthBridge(iframe) {
    if (!iframe || findIframeBridge(iframe)) return;
    var src = iframe.src || iframe.getAttribute("src") || "";
    if (!src) return;
    var target = parseTiquoEmbedTarget(src);
    if (!target) return;

    var lastRequestNonce = null;
    var lastAcknowledgedNonce = null;

    function ping() {
      if (!iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        { type: IFRAME_AUTH_PING, version: IFRAME_AUTH_VERSION },
        target.origin,
      );
    }

    function onMessage(event) {
      if (event.source !== iframe.contentWindow || event.origin !== target.origin) {
        return;
      }

      var message = event.data;
      if (
        message &&
        message.type === "iframe-resize" &&
        iframe.getAttribute("data-tiquo-auto-resize") !== "false"
      ) {
        var height = Number(message.height);
        if (Number.isFinite(height) && height > 0) {
          iframe.style.height = Math.ceil(height) + "px";
        }
        return;
      }

      if (
        !message ||
        typeof message !== "object" ||
        message.type !== IFRAME_AUTH_READY ||
        message.version !== IFRAME_AUTH_VERSION ||
        typeof message.organizationId !== "string" ||
        !message.organizationId ||
        !isSafeIframeNonce(message.requestNonce)
      ) {
        return;
      }

      if (lastAcknowledgedNonce !== message.requestNonce) {
        lastAcknowledgedNonce = message.requestNonce;
        ping();
      }
      if (!accessToken || lastRequestNonce === message.requestNonce) return;
      lastRequestNonce = message.requestNonce;

      getIframeToken(target.flowId, 1)
        .then(function (result) {
          if (
            event.source !== iframe.contentWindow ||
            !result ||
            result.organizationId !== message.organizationId
          ) {
            log("Refused iframe auth handoff for a different organization");
            return;
          }
          iframe.contentWindow.postMessage(
            {
              type: IFRAME_AUTH_RESPONSE,
              version: IFRAME_AUTH_VERSION,
              organizationId: result.organizationId,
              requestNonce: message.requestNonce,
              token: result.token,
            },
            target.origin,
          );
        })
        .catch(function (error) {
          lastRequestNonce = null;
          log("Failed to authenticate iframe", error);
        });
    }

    function onLoad() {
      ping();
    }

    window.addEventListener("message", onMessage);
    iframe.addEventListener("load", onLoad);
    iframeBridges.push({
      iframe: iframe,
      ping: ping,
      cleanup: function () {
        window.removeEventListener("message", onMessage);
        iframe.removeEventListener("load", onLoad);
      },
    });
    // Payment-method embeds report saved/removed/default changes.
    if (target.flowId === "payment-methods") installPaymentMethodEventListener();

    Promise.resolve().then(ping);
  }

  function scanAndInjectIframes() {
    var iframes = document.querySelectorAll("iframe");
    for (var index = 0; index < iframes.length; index += 1) {
      registerIframeAuthBridge(iframes[index]);
    }
  }

  function pingRegisteredIframes() {
    iframeBridges.forEach(function (bridge) {
      bridge.ping();
    });
  }

  function observeNewIframes() {
    if (typeof MutationObserver === "undefined" || iframeObserver) return;
    iframeObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        for (var index = 0; index < mutation.addedNodes.length; index += 1) {
          var node = mutation.addedNodes[index];
          if (node && node.tagName === "IFRAME") {
            registerIframeAuthBridge(node);
          } else if (node && typeof node.querySelectorAll === "function") {
            var nested = node.querySelectorAll("iframe");
            for (var nestedIndex = 0; nestedIndex < nested.length; nestedIndex += 1) {
              registerIframeAuthBridge(nested[nestedIndex]);
            }
          }
        }
        for (var removedIndex = 0; removedIndex < mutation.removedNodes.length; removedIndex += 1) {
          var removedNode = mutation.removedNodes[removedIndex];
          for (var bridgeIndex = iframeBridges.length - 1; bridgeIndex >= 0; bridgeIndex -= 1) {
            var bridge = iframeBridges[bridgeIndex];
            if (
              bridge.iframe === removedNode ||
              (removedNode &&
                typeof removedNode.contains === "function" &&
                removedNode.contains(bridge.iframe))
            ) {
              bridge.cleanup();
              iframeBridges.splice(bridgeIndex, 1);
            }
          }
        }
      });
    });
    iframeObserver.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function autoInjectIframeTokens() {
    window.__tiquo_get_iframe_token = function () {
      return getIframeToken();
    };
    onReady(function () {
      scanAndInjectIframes();
      observeNewIframes();
    });
  }

  function embedCustomerFlow(flowUrl, container, options) {
    options = options || {};
    var containerElement =
      typeof container === "string" ? document.querySelector(container) : container;
    if (!containerElement) return Promise.reject(new Error("Container element not found"));

    var url;
    try {
      url = new URL(flowUrl, window.location.href);
    } catch {
      return Promise.reject(new Error("Invalid customer flow URL"));
    }
    if (!parseTiquoEmbedTarget(url.toString())) {
      return Promise.reject(new Error("Only Tiquo embed URLs can be embedded"));
    }

    var iframe = document.createElement("iframe");
    iframe.src = url.toString();
    iframe.style.width = options.width || "100%";
    iframe.style.height = options.height || options.minHeight || "200px";
    iframe.style.minHeight = options.minHeight || "200px";
    iframe.style.border = "none";
    iframe.style.display = "block";
    iframe.style.overflow = "hidden";
    iframe.style.transition = "height 150ms ease";
    iframe.setAttribute(
      "data-tiquo-auto-resize",
      options.autoResize === false ? "false" : "true",
    );
    iframe.setAttribute("allow", "payment *; web-share");
    iframe.setAttribute("loading", "lazy");
    registerIframeAuthBridge(iframe);

    if (typeof options.onLoad === "function") {
      iframe.addEventListener("load", options.onLoad);
    }
    if (typeof options.onError === "function") {
      iframe.addEventListener("error", function () {
        options.onError(new Error("Failed to load iframe"));
      });
    }

    containerElement.innerHTML = "";
    containerElement.appendChild(iframe);
    return Promise.resolve(iframe);
  }

  // "30 min", "1 h", "1 h 30 min" (mirrors lib/booking-length.ts).
  function formatBookingLengthMinutes(minutes) {
    var hours = Math.floor(minutes / 60);
    var rest = minutes % 60;
    if (hours === 0) return rest + " min";
    if (rest === 0) return hours + " h";
    return hours + " h " + rest + " min";
  }

  function embedServiceBooking(bookingConfigId, serviceId, container, options) {
    var normalizedBookingConfigId = String(bookingConfigId || "").trim();
    var normalizedServiceId = String(serviceId || "").trim();
    if (!normalizedBookingConfigId || !normalizedServiceId) {
      return Promise.reject(
        new Error("Customer flow ID and service ID are required"),
      );
    }

    var bookingUrl = new URL(
      "https://book.tiquo.app/embed/" +
        encodeURIComponent(normalizedBookingConfigId),
    );
    bookingUrl.searchParams.set("serviceId", normalizedServiceId);
    bookingUrl.searchParams.set("lockService", "true");
    // Carry a booking length chosen on a custom catalog into the booking.
    var lengthMinutes = options && options.lengthMinutes;
    var lengthOptionId = options && options.lengthOptionId;
    if (Number.isInteger(lengthMinutes) && lengthMinutes > 0) {
      bookingUrl.searchParams.set("length", String(lengthMinutes));
    }
    if (lengthOptionId) {
      bookingUrl.searchParams.set("lengthOptionId", String(lengthOptionId));
    }
    // Carry the date, time and party size chosen on a custom page, so the
    // customer doesn't choose them again. The flow re-checks availability.
    var date = options && options.date ? String(options.date).trim() : "";
    var startTime = options && options.startTime ? String(options.startTime).trim() : "";
    var partySize = options && options.partySize;
    if (
      date &&
      !(
        /^\d{4}-\d{2}-\d{2}$/.test(date) &&
        Number.isFinite(Date.parse(date + "T00:00:00Z")) &&
        new Date(Date.parse(date + "T00:00:00Z")).toISOString().slice(0, 10) === date
      )
    ) {
      return Promise.reject(new Error("date must be a calendar date formatted as YYYY-MM-DD"));
    }
    if (startTime && !/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) {
      return Promise.reject(new Error("startTime must be formatted as HH:mm"));
    }
    if (startTime && !date) {
      return Promise.reject(new Error("startTime needs a date"));
    }
    if (
      partySize !== undefined &&
      partySize !== null &&
      (!Number.isInteger(partySize) || partySize < 1 || partySize > 1000)
    ) {
      return Promise.reject(new Error("partySize must be an integer from 1 to 1000"));
    }
    if (date) bookingUrl.searchParams.set("date", date);
    if (startTime) bookingUrl.searchParams.set("startTime", startTime);
    if (partySize !== undefined && partySize !== null) {
      bookingUrl.searchParams.set("partySize", String(partySize));
    }
    return embedCustomerFlow(bookingUrl.toString(), container, options);
  }

  function embedMembership(planId, container, options) {
    var normalizedPlanId = String(planId || "").trim();
    if (!normalizedPlanId) {
      return Promise.reject(new Error("Membership plan ID is required"));
    }
    return embedCustomerFlow(
      "https://book.tiquo.app/embed/membership/" + encodeURIComponent(normalizedPlanId),
      container,
      options,
    );
  }

  function refreshTokenIfNeeded() {
    if (refreshPromise) return refreshPromise;
    if (!refreshToken) return Promise.resolve(false);

    var tokenBeingRefreshed = refreshToken;
    refreshPromise = withCrossTabRefreshLock(function () {
      if (adoptPeerRefreshedTokens(tokenBeingRefreshed)) return true;
      if (!refreshToken) return false;
      return performTokenRefresh(refreshToken);
    }).finally(function () {
      refreshPromise = null;
    });
    return refreshPromise;
  }

  function performTokenRefresh(tokenBeingRefreshed) {
    return jsonRequest("/api/client/v1/refresh", { refresh_token: tokenBeingRefreshed })
      .then(expectOk("Failed to refresh session"))
      .then(function (result) {
        if (result.data && result.data.access_token) {
          accessToken = result.data.access_token;
          refreshToken = result.data.refresh_token || refreshToken;
          saveTokens();
          updateSessionExpiryFromAccessToken();
          scheduleRefresh();
          return true;
        }
        return recoverNewerStoredTokens(tokenBeingRefreshed);
      })
      .catch(function () {
        return recoverNewerStoredTokens(tokenBeingRefreshed);
      });
  }

  function withCrossTabRefreshLock(callback) {
    // Refresh tokens rotate on every use. Serialize renewal across tabs so a
    // second tab never replays the just-consumed token and revokes the session.
    var lockManager = navigator && navigator.locks;
    if (!lockManager || typeof lockManager.request !== "function") {
      return Promise.resolve().then(callback);
    }
    return lockManager.request(storagePrefix + "refresh", callback);
  }

  function adoptPeerRefreshedTokens(tokenBeingRefreshed) {
    var storedAccessToken = safeStorage(
      window.localStorage,
      storagePrefix + "access_token",
    );
    var storedRefreshToken = safeStorage(
      window.localStorage,
      storagePrefix + "refresh_token",
    );
    if (!storedRefreshToken || storedRefreshToken === tokenBeingRefreshed) {
      return false;
    }

    accessToken = storedAccessToken;
    refreshToken = storedRefreshToken;
    updateSessionExpiryFromAccessToken();
    scheduleRefresh();
    return !!accessToken;
  }

  function updateSessionExpiryFromAccessToken() {
    if (!session || !accessToken) return;
    var payload = decodeJwt(accessToken);
    if (payload && payload.exp) session.expiresAt = payload.exp * 1000;
  }

  function recoverNewerStoredTokens(failedRefreshToken) {
    var storedAccessToken = safeStorage(
      window.localStorage,
      storagePrefix + "access_token",
    );
    var storedRefreshToken = safeStorage(
      window.localStorage,
      storagePrefix + "refresh_token",
    );

    if (storedRefreshToken && storedRefreshToken !== failedRefreshToken) {
      accessToken = storedAccessToken;
      refreshToken = storedRefreshToken;
      updateSessionExpiryFromAccessToken();
      scheduleRefresh();
      return !!accessToken;
    }

    clearTokens();
    return false;
  }

  function getOrders(options) {
    return authenticatedGet("/api/client/v1/orders", options).then(function (result) {
      return result.data || { orders: [], hasMore: false };
    });
  }

  function getBookings(options) {
    return authenticatedGet("/api/client/v1/bookings", options).then(function (result) {
      return result.data || { bookings: [], hasMore: false };
    });
  }

  function cancelBooking(bookingId) {
    return bookingPost("/api/client/v1/bookings/cancel", { bookingId: bookingId });
  }

  function getBookingRescheduleOptions(bookingId, options) {
    return authenticatedGet(
      "/api/client/v1/bookings/reschedule-options",
      extend({}, options || {}, { bookingId: bookingId }),
    ).then(function (result) {
      return result.data;
    });
  }

  function rescheduleBooking(bookingId, data) {
    return bookingPost("/api/client/v1/bookings/reschedule", {
      bookingId: bookingId,
      date: data && data.date,
      startTime: data && data.startTime,
    });
  }

  function bookingPost(path, body) {
    return ensureValidToken().then(function () {
      if (!accessToken) throw new Error("Not authenticated");
      return fetch(apiEndpoint + path, {
        method: "POST",
        headers: { Authorization: "Bearer " + accessToken, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      }).then(expectOk("Booking request failed"));
    }).then(function (result) {
      return result.data;
    });
  }

  function getMemberships() {
    if (membershipDataPromise && membershipDataToken === accessToken) {
      return membershipDataPromise;
    }

    membershipDataToken = accessToken;
    membershipDataPromise = authenticatedGet("/api/client/v1/memberships")
      .then(function (result) {
        membershipDataToken = accessToken;
        return extend({ memberships: [], availablePlans: [], permissions: [] }, result.data || {});
      })
      .catch(function (error) {
        membershipDataPromise = null;
        membershipDataToken = null;
        throw error;
      });
    return membershipDataPromise;
  }

  // Membership permission names the signed-in customer holds now, from the
  // same request as getMemberships().
  function getPermissions() {
    return getMemberships().then(function (result) {
      return result.permissions || [];
    });
  }

  // Names match ignoring case and surrounding spaces; signed out holds none.
  function hasPermission(name) {
    var wanted = String(name || "").trim().toLowerCase();
    if (!wanted || !(accessToken || refreshToken)) return Promise.resolve(false);
    return getPermissions().then(function (permissions) {
      return permissions.some(function (permission) {
        return String(permission).trim().toLowerCase() === wanted;
      });
    });
  }

  function getGuestFlows(options) {
    if (!(options && options.refresh) && guestFlowDataPromise && guestFlowDataToken === accessToken) {
      return guestFlowDataPromise;
    }

    guestFlowDataToken = accessToken;
    guestFlowDataPromise = authenticatedGet("/api/client/v1/guest-flows")
      .then(function (result) {
        guestFlowDataToken = accessToken;
        return result.data || { guestFlows: [] };
      })
      .catch(function (error) {
        guestFlowDataPromise = null;
        guestFlowDataToken = null;
        throw error;
      });
    return guestFlowDataPromise;
  }

  // Guest Pass invitations sent or received, not balances (see getPassBalances).
  function getGuestPasses() {
    return authenticatedGet("/api/client/v1/guest-passes").then(function (result) {
      return result.data || { guestPasses: [] };
    });
  }

  // Access Pass and Guest Pass balances: uses held per pass type, not invitations.
  function getPassBalances() {
    return authenticatedGet("/api/client/v1/pass-balances").then(function (result) {
      return result.data || {
        accessPassBalance: { total: 0, passTypes: [] },
        guestPassBalance: { total: 0, heldByInvitations: 0, invitationsUseGuestPasses: false, passTypes: [] },
      };
    });
  }

  function getGuestFlow(flowId) {
    return getGuestFlows({ refresh: true }).then(function (result) {
      return (result.guestFlows || []).find(function (flow) { return flow.id === flowId; }) || null;
    });
  }

  function createGuestInvitations(input) {
    return guestInvitationPost("/api/client/v1/guest-invitations", input);
  }

  function getGuestInvitation(token) {
    return authenticatedGet("/api/client/v1/guest-invitations", { token: token }).then(function (result) {
      return result.data.invitation;
    });
  }

  // The latest 100 invitations the customer sent, with tokens, links and history.
  function getGuestInvitations() {
    return authenticatedGet("/api/client/v1/guest-invitations").then(function (result) {
      return result.data || { invitations: [] };
    });
  }

  function acceptGuestInvitation(token) {
    return guestInvitationPost("/api/client/v1/guest-invitations/accept", { token: token });
  }

  function guestInvitationPost(path, body) {
    return ensureValidToken().then(function () {
      if (!accessToken) throw new Error("Not authenticated");
      return fetch(apiEndpoint + path, {
        method: "POST",
        headers: { Authorization: "Bearer " + accessToken, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      }).then(expectOk("Guest Pre-Registration request failed"));
    }).then(function (result) {
      return result.data;
    }).finally(function () {
      // Reservations and acceptance change pass availability, including after uncertain responses.
      guestFlowDataPromise = null;
      guestFlowDataToken = null;
      guestPassRevision += 1;
      renderGuestPassLists();
      renderPassBalances();
    });
  }

  function getFlowServiceAuthKey() {
    if (!accessToken) return "anonymous";
    var payload = decodeJwt(accessToken);
    return (
      "customer:" +
      String(
        (payload && (payload.org_customer_id || payload.sub)) ||
          "authenticated",
      )
    );
  }

  function getPolicies() {
    var url = new URL(apiEndpoint + "/api/auth-dom/policies");
    url.searchParams.set("publicKey", publicKey);
    return fetch(url.toString(), {
      method: "GET",
      headers: { "X-Public-Key": publicKey },
      credentials: "include",
    }).then(expectOk("Failed to get business policies"));
  }

  function getPolicy(id) {
    return getPolicies().then(function (result) {
      return result.policies.find(function (policy) {
        return policy.id === String(id || "").trim();
      }) || null;
    });
  }

  function renderBusinessPolicies() {
    var containers = selectAll("[data-tiquo-policy]").filter(function (container) {
      var id = String(container.getAttribute("data-tiquo-policy") || "").trim();
      if (!id || container.__tiquoPolicyId === id) return false;
      container.__tiquoPolicyId = id;
      return true;
    });
    if (!containers.length) return;
    getPolicies().then(function (result) {
      containers.forEach(function (container) {
        var id = String(container.getAttribute("data-tiquo-policy") || "").trim();
        var policy = result.policies.find(function (item) { return item.id === id; });
        container.textContent = policy ? policy.content : "";
        container.style.whiteSpace = "pre-wrap";
        container.setAttribute("data-tiquo-policy-state", policy ? "ready" : "empty");
      });
    }).catch(function (error) {
      containers.forEach(function (container) {
        container.setAttribute("data-tiquo-policy-state", "error");
      });
      log("Failed to render business policies", error);
    });
  }

  function getFlowServices(bookingConfigId) {
    var normalizedBookingConfigId = String(bookingConfigId || "").trim();
    if (!normalizedBookingConfigId) {
      return Promise.reject(new Error("Customer flow ID is required"));
    }

    var authKey = getFlowServiceAuthKey();
    var cacheKey = normalizedBookingConfigId + "|" + authKey;
    if (flowServiceDataPromises[cacheKey]) {
      return flowServiceDataPromises[cacheKey];
    }

    var prepareSession =
      accessToken || refreshToken
        ? ensureValidToken().catch(function () {
            return false;
          })
        : Promise.resolve(false);

    flowServiceDataPromises[cacheKey] = prepareSession
      .then(function () {
        var url = new URL(apiEndpoint + "/api/auth-dom/flow-services");
        url.searchParams.set("bookingConfigId", normalizedBookingConfigId);
        url.searchParams.set("publicKey", publicKey);
        var headers = { "X-Public-Key": publicKey };
        if (accessToken) headers.Authorization = "Bearer " + accessToken;

        return fetch(url.toString(), {
          method: "GET",
          headers: headers,
          credentials: "include",
        }).then(expectOk("Failed to get customer flow services"));
      })
      .catch(function (error) {
        delete flowServiceDataPromises[cacheKey];
        throw error;
      });

    return flowServiceDataPromises[cacheKey];
  }

  function getFlowAvailability(bookingConfigId, options) {
    var normalizedBookingConfigId = String(bookingConfigId || "").trim();
    var normalizedOptions = options || {};
    var dateFrom = String(normalizedOptions.dateFrom || "").trim();
    var dateTo = String(normalizedOptions.dateTo || "").trim();
    var partySize = normalizedOptions.partySize;
    var includeSlots = normalizedOptions.includeSlots;
    var lengthMinutes = normalizedOptions.lengthMinutes;
    if (!normalizedBookingConfigId) {
      return Promise.reject(new Error("Customer flow ID is required"));
    }
    if (!dateFrom || !dateTo) {
      return Promise.reject(new Error("dateFrom and dateTo are required"));
    }
    var dateFromTs = Date.parse(dateFrom + "T00:00:00.000Z");
    var dateToTs = Date.parse(dateTo + "T00:00:00.000Z");
    var datesAreValid =
      Number.isFinite(dateFromTs) &&
      Number.isFinite(dateToTs) &&
      new Date(dateFromTs).toISOString().slice(0, 10) === dateFrom &&
      new Date(dateToTs).toISOString().slice(0, 10) === dateTo;
    if (
      datesAreValid &&
      dateToTs - dateFromTs >=
        FLOW_AVAILABILITY_MAX_DAYS * 24 * 60 * 60 * 1000
    ) {
      return Promise.reject(
        new Error(
          "Flow availability requests cannot exceed " +
            FLOW_AVAILABILITY_MAX_DAYS +
            " days",
        ),
      );
    }
    if (
      datesAreValid &&
      includeSlots === true &&
      dateToTs !== dateFromTs
    ) {
      return Promise.reject(
        new Error("Slot detail requests cannot exceed 1 day"),
      );
    }
    if (
      partySize !== undefined &&
      (!Number.isInteger(partySize) || partySize < 1 || partySize > 1000)
    ) {
      return Promise.reject(
        new Error("partySize must be an integer from 1 to 1000"),
      );
    }
    if (includeSlots !== undefined && typeof includeSlots !== "boolean") {
      return Promise.reject(new Error("includeSlots must be a boolean"));
    }
    if (
      lengthMinutes !== undefined &&
      (!Number.isInteger(lengthMinutes) ||
        lengthMinutes < 5 ||
        lengthMinutes > 1440)
    ) {
      return Promise.reject(
        new Error("lengthMinutes must be a whole number of minutes up to 24 hours"),
      );
    }

    var prepareSession =
      accessToken || refreshToken
        ? ensureValidToken().catch(function () {
            return false;
          })
        : Promise.resolve(false);

    return prepareSession.then(function () {
      var url = new URL(apiEndpoint + "/api/auth-dom/flow-availability");
      url.searchParams.set("bookingConfigId", normalizedBookingConfigId);
      url.searchParams.set("publicKey", publicKey);
      url.searchParams.set("dateFrom", dateFrom);
      url.searchParams.set("dateTo", dateTo);
      if (partySize !== undefined) {
        url.searchParams.set("partySize", String(partySize));
      }
      if (includeSlots === true) {
        url.searchParams.set("includeSlots", "true");
      }
      if (lengthMinutes !== undefined) {
        url.searchParams.set("lengthMinutes", String(lengthMinutes));
      }
      var headers = { "X-Public-Key": publicKey };
      if (accessToken) headers.Authorization = "Bearer " + accessToken;

      return fetch(url.toString(), {
        method: "GET",
        headers: headers,
        credentials: "include",
      }).then(expectOk("Failed to get customer flow availability"));
    });
  }

  function getFlowService(bookingConfigId, serviceId) {
    var normalizedServiceId = String(serviceId || "").trim();
    if (!normalizedServiceId) return Promise.resolve(null);
    return getFlowServices(bookingConfigId).then(function (catalog) {
      var services = catalog.services || [];
      for (var index = 0; index < services.length; index += 1) {
        if (String(services[index].id) === normalizedServiceId) {
          return services[index];
        }
      }
      return null;
    });
  }

  function getWalletCardLinks(options) {
    var designName = typeof options === "string" ? options : options && options.designName;
    if (!designName) return Promise.reject(new Error("designName is required"));
    return authenticatedGet("/api/client/v1/wallet-card-links", {
      designName: designName,
    }).then(function (result) {
      return result.data || result;
    });
  }

  // ---- Saved payment methods ------------------------------------------------

  function getPaymentMethods() {
    var key = accessToken + "|" + paymentMethodsRevision;
    if (paymentMethodDataPromise && paymentMethodDataKey === key) {
      return paymentMethodDataPromise;
    }
    paymentMethodDataKey = key;
    paymentMethodDataPromise = paymentMethodRequest("GET", null, "Failed to get payment methods")
      .then(function (result) {
        return { paymentMethods: Array.isArray(result.paymentMethods) ? result.paymentMethods : [] };
      })
      .catch(function (error) {
        paymentMethodDataPromise = null;
        paymentMethodDataKey = null;
        throw error;
      });
    return paymentMethodDataPromise;
  }

  function setDefaultPaymentMethod(paymentMethodId) {
    if (!isPaymentMethodId(paymentMethodId)) {
      return Promise.reject(new Error("A valid paymentMethodId (pm_...) is required"));
    }
    return paymentMethodRequest(
      "PATCH",
      { paymentMethodId: paymentMethodId },
      "Failed to update default payment method",
    ).then(function (result) {
      invalidatePaymentMethods();
      return { preservedMembershipCount: Number(result.preservedMembershipCount) || 0 };
    });
  }

  function removePaymentMethod(paymentMethodId) {
    if (!isPaymentMethodId(paymentMethodId)) {
      return Promise.reject(new Error("A valid paymentMethodId (pm_...) is required"));
    }
    return paymentMethodRequest(
      "DELETE",
      { paymentMethodId: paymentMethodId },
      "Failed to remove payment method",
    ).then(function (result) {
      invalidatePaymentMethods();
      return isPaymentMethodId(result.replacementPaymentMethodId)
        ? { replacementPaymentMethodId: result.replacementPaymentMethodId }
        : {};
    });
  }

  function paymentMethodRequest(method, body, defaultMessage) {
    return ensureValidToken()
      .then(function () {
        if (!accessToken) throw new Error("Not authenticated");
        var init = {
          method: method,
          headers: { Authorization: "Bearer " + accessToken },
          credentials: "include",
        };
        if (body) {
          init.headers["Content-Type"] = "application/json";
          init.body = JSON.stringify(body);
        }
        return fetch(apiEndpoint + "/api/client/v1/payment-methods", init).then(
          expectOk(defaultMessage),
        );
      })
      .then(function (result) {
        if (!result || result.success === false) {
          throw new Error((result && result.error) || defaultMessage);
        }
        return result;
      });
  }

  function isPaymentMethodId(value) {
    return typeof value === "string" && /^pm_[A-Za-z0-9]+$/.test(value);
  }

  function invalidatePaymentMethods() {
    paymentMethodsRevision += 1;
    paymentMethodDataPromise = null;
    paymentMethodDataKey = null;
    scheduleRender();
  }

  function buildPaymentMethodsEmbedUrl(view, options) {
    if (!/^pk_[A-Za-z0-9_-]{1,200}$/.test(String(publicKey || ""))) return null;
    var url = new URL(
      "https://book.tiquo.app/embed/payment-methods/" +
        encodeURIComponent(publicKey) +
        (view === "add" ? "/add" : ""),
    );
    var theme = options && typeof options.theme === "string" ? options.theme.trim() : "";
    if (theme && theme.length <= 200) url.searchParams.set("theme", theme);
    if (view === "add" && options && options.setAsDefault === true) {
      url.searchParams.set("default", "true");
    }
    return url.toString();
  }

  function embedPaymentMethods(container, options) {
    options = options || {};
    var url = buildPaymentMethodsEmbedUrl("manage", options);
    if (!url) return Promise.reject(new Error("Invalid public key"));
    return embedCustomerFlow(url, container, options).then(function (iframe) {
      iframe.__tiquoPaymentMethodHandlers = {
        onSaved: options.onSaved,
        onRemoved: options.onRemoved,
        onDefaultChanged: options.onDefaultChanged,
      };
      installPaymentMethodEventListener();
      return iframe;
    });
  }

  function embedCardForm(container, options) {
    options = options || {};
    var url = buildPaymentMethodsEmbedUrl("add", options);
    if (!url) return Promise.reject(new Error("Invalid public key"));
    return embedCustomerFlow(url, container, options).then(function (iframe) {
      iframe.__tiquoPaymentMethodHandlers = { onSaved: options.onSaved };
      installPaymentMethodEventListener();
      return iframe;
    });
  }

  function parsePaymentMethodEvent(data) {
    if (!data || typeof data !== "object" || data.version !== PAYMENT_METHOD_EVENT_VERSION) {
      return null;
    }
    var type = Object.prototype.hasOwnProperty.call(PAYMENT_METHOD_EVENTS, data.type)
      ? PAYMENT_METHOD_EVENTS[data.type]
      : null;
    if (type === "saved") {
      var method = normalizePaymentMethod(data.paymentMethod);
      return method ? { type: type, eventName: data.type, paymentMethod: method } : null;
    }
    if ((type === "removed" || type === "default-changed") && isPaymentMethodId(data.paymentMethodId)) {
      var event = { type: type, eventName: data.type, paymentMethodId: data.paymentMethodId };
      if (type === "removed" && isPaymentMethodId(data.replacementPaymentMethodId)) {
        event.replacementPaymentMethodId = data.replacementPaymentMethodId;
      }
      return event;
    }
    return null;
  }

  function normalizePaymentMethod(raw) {
    if (!raw || typeof raw !== "object" || !isPaymentMethodId(raw.id)) return null;
    var method = { id: raw.id, isDefault: raw.isDefault === true };
    if (raw.kind === "card" || raw.kind === "direct_debit") method.kind = raw.kind;
    ["label", "brand", "last4", "bankName"].forEach(function (key) {
      if (typeof raw[key] === "string" && raw[key]) method[key] = raw[key];
    });
    if (Number.isInteger(raw.expMonth)) method.expMonth = raw.expMonth;
    if (Number.isInteger(raw.expYear)) method.expYear = raw.expYear;
    if (raw.cardUseType === "business" || raw.cardUseType === "personal") {
      method.cardUseType = raw.cardUseType;
    }
    return method;
  }

  // One listener for every registered Tiquo iframe, so static
  // <iframe src="https://book.tiquo.app/embed/payment-methods/..."> snippets
  // get the same DOM events as embedPaymentMethods()/embedCardForm().
  function installPaymentMethodEventListener() {
    if (paymentMethodListenerInstalled) return;
    paymentMethodListenerInstalled = true;
    window.addEventListener("message", function (event) {
      var iframe = null;
      for (var index = 0; index < iframeBridges.length; index += 1) {
        var candidate = iframeBridges[index].iframe;
        if (candidate && candidate.contentWindow === event.source) {
          iframe = candidate;
          break;
        }
      }
      if (!iframe) return;
      var target = parseTiquoEmbedTarget(iframe.src || iframe.getAttribute("src") || "");
      if (!target || event.origin !== target.origin) return;
      var parsed = parsePaymentMethodEvent(event.data);
      if (!parsed) return;

      invalidatePaymentMethods();
      var detail =
        parsed.type === "saved"
          ? { paymentMethod: parsed.paymentMethod }
          : parsed.replacementPaymentMethodId
            ? { paymentMethodId: parsed.paymentMethodId, replacementPaymentMethodId: parsed.replacementPaymentMethodId }
            : { paymentMethodId: parsed.paymentMethodId };
      var handlers = iframe.__tiquoPaymentMethodHandlers || {};
      try {
        if (parsed.type === "saved" && typeof handlers.onSaved === "function") {
          handlers.onSaved(parsed.paymentMethod);
        }
        if (parsed.type === "removed" && typeof handlers.onRemoved === "function") {
          handlers.onRemoved(detail);
        }
        if (parsed.type === "default-changed" && typeof handlers.onDefaultChanged === "function") {
          handlers.onDefaultChanged(parsed.paymentMethodId);
        }
      } catch (error) {
        log("Payment method event handler failed", error);
      }
      dispatchProfileFormEvent(iframe, parsed.eventName, detail);
    });
  }

  function mountPaymentMethodEmbeds() {
    selectAll("[data-tiquo-embed='payment-methods'],[data-tiquo-embed='card-form']").forEach(
      function (container) {
        if (container.__tiquoPaymentMethodEmbedMounted) return;
        container.__tiquoPaymentMethodEmbedMounted = true;
        var options = {};
        var theme = container.getAttribute("data-tiquo-theme");
        if (theme) options.theme = theme;
        var minHeight = container.getAttribute("data-tiquo-min-height");
        if (minHeight) options.minHeight = minHeight;
        var isCardForm = container.getAttribute("data-tiquo-embed") === "card-form";
        if (isCardForm) {
          options.setAsDefault = container.getAttribute("data-tiquo-set-default") === "true";
        }
        (isCardForm ? embedCardForm : embedPaymentMethods)(container, options).catch(function (error) {
          container.__tiquoPaymentMethodEmbedMounted = false;
          log("Failed to embed payment methods", error);
        });
      },
    );
  }

  function renderPaymentMethodLists() {
    selectAll("[data-tiquo-payment-methods]").forEach(function (container) {
      if (container.__tiquoLoadingPaymentMethods) return;
      var template = getTemplate(container, "[data-tiquo-payment-method-item]");
      if (!template) return;
      var limit = readPositiveInteger(container.getAttribute("data-tiquo-limit"), 20);
      var renderKey = [
        session && session.user ? session.user.id : "logged-out",
        paymentMethodsRevision,
        limit,
      ].join("|");
      if (container.__tiquoRenderedPaymentMethodsFor === renderKey) return;
      if (!session) {
        container.__tiquoRenderedPaymentMethodsFor = renderKey;
        renderEmptyList(container, template);
        return;
      }

      container.__tiquoLoadingPaymentMethods = true;
      getPaymentMethods()
        .then(function (result) {
          var methods = result.paymentMethods || [];
          container.__tiquoRenderedPaymentMethodsFor = renderKey;
          renderList(
            container,
            template,
            methods.slice(0, limit).map(normalizePaymentMethodForRender),
            "[data-tiquo-payment-method]",
            "data-tiquo-payment-method",
            null,
            function (node, method) {
              bindPaymentMethodActions(node, method, methods.length);
            },
          );
        })
        .catch(function (error) {
          log("Failed to render payment methods", error);
        })
        .finally(function () {
          container.__tiquoLoadingPaymentMethods = false;
        });
    });
  }

  function formatPaymentMethodBrand(brand) {
    if (!brand) return "Card";
    if (String(brand).toLowerCase() === "amex") return "American Express";
    return String(brand).charAt(0).toUpperCase() + String(brand).slice(1);
  }

  // Mirrors paymentMethodTitle/paymentMethodSubtitle in tiquo-customer.
  function normalizePaymentMethodForRender(method) {
    var isDirectDebit = method.kind === "direct_debit";
    var expiry =
      method.expMonth && method.expYear
        ? String(method.expMonth).padStart(2, "0") + "/" + method.expYear
        : "";
    return extend({}, method, {
      brandName: isDirectDebit ? "Direct debit" : formatPaymentMethodBrand(method.brand),
      title: isDirectDebit
        ? method.label || "Direct debit" + (method.last4 ? " ending in " + method.last4 : "")
        : formatPaymentMethodBrand(method.brand) + " ending in " + (method.last4 || "****"),
      subtitle: isDirectDebit ? method.bankName || "Direct debit" : expiry ? "Expires " + expiry : "",
      expiry: expiry,
      defaultLabel: method.isDefault ? "Default" : "",
    });
  }

  function bindPaymentMethodActions(node, method, methodCount) {
    selectIncludingRoot("[data-tiquo-payment-method-action]", node).forEach(function (button) {
      var action = String(button.getAttribute("data-tiquo-payment-method-action") || "")
        .trim()
        .toLowerCase();
      if (action === "make-default" && method.isDefault) {
        setElementVisible(button, false);
        return;
      }
      if (action === "remove" && methodCount <= 1) {
        // The Customer API never removes the last saved method.
        if ("disabled" in button) button.disabled = true;
        button.setAttribute("aria-disabled", "true");
        button.setAttribute("title", "Add another payment method before removing this one.");
        return;
      }
      if (action !== "make-default" && action !== "remove") return;
      button.addEventListener("click", function (event) {
        event.preventDefault();
        if (button.__tiquoPaymentMethodBusy) return;
        var confirmText = button.getAttribute("data-tiquo-confirm");
        if (confirmText && typeof window.confirm === "function" && !window.confirm(confirmText)) {
          return;
        }
        button.__tiquoPaymentMethodBusy = true;
        if ("disabled" in button) button.disabled = true;
        button.setAttribute("aria-busy", "true");
        var request =
          action === "remove" ? removePaymentMethod(method.id) : setDefaultPaymentMethod(method.id);
        request
          .then(function (result) {
            dispatchProfileFormEvent(
              button,
              action === "remove" ? "tiquo:payment-method-removed" : "tiquo:payment-method-default-changed",
              extend({ paymentMethodId: method.id }, result || {}),
            );
          })
          .catch(function (error) {
            dispatchProfileFormEvent(button, "tiquo:payment-method-error", {
              error: error,
              action: action,
              paymentMethodId: method.id,
            });
          })
          .finally(function () {
            button.__tiquoPaymentMethodBusy = false;
            if ("disabled" in button) button.disabled = false;
            button.setAttribute("aria-busy", "false");
          });
      });
    });
  }

  function authenticatedGet(path, options) {
    return ensureValidToken().then(function () {
      if (!accessToken) throw new Error("Not authenticated");
      var url = new URL(apiEndpoint + path);
      Object.keys(options || {}).forEach(function (key) {
        var value = options[key];
        if (value !== undefined && value !== null && value !== "") {
          url.searchParams.set(key, String(value));
        }
      });
      return fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: "Bearer " + accessToken },
        credentials: "include",
      }).then(expectOk("Request failed"));
    });
  }

  function expectOk(defaultMessage) {
    return function (response) {
      if (!response.ok) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (error) {
            throw new Error(error.error || defaultMessage);
          });
      }
      return response.json();
    };
  }

  function saveTokens() {
    safeStorage(window.localStorage, storagePrefix + "access_token", accessToken);
    safeStorage(window.localStorage, storagePrefix + "refresh_token", refreshToken);
  }

  function restoreTokens() {
    accessToken = safeStorage(window.localStorage, storagePrefix + "access_token");
    refreshToken = safeStorage(window.localStorage, storagePrefix + "refresh_token");
  }

  function installSessionLifecycleListeners() {
    window.addEventListener("storage", function (event) {
      if (
        !event ||
        (event.key !== storagePrefix + "access_token" &&
          event.key !== storagePrefix + "refresh_token")
      ) {
        return;
      }
      scheduleStoredSessionSync();
    });

    window.addEventListener("pageshow", function (event) {
      if (!event || !event.persisted) return;
      scheduleStoredSessionSync(true);
    });

    window.addEventListener("focus", function () {
      scheduleStoredSessionSync(true);
    });

    window.addEventListener("online", function () {
      scheduleStoredSessionSync(true);
    });

    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "visible") {
        scheduleStoredSessionSync(true);
      }
    });
  }

  function scheduleStoredSessionSync(forceRefresh) {
    if (sessionSyncTimer) clearTimeout(sessionSyncTimer);
    sessionSyncTimer = setTimeout(function () {
      sessionSyncTimer = null;
      var previousAccessToken = accessToken;
      var previousRefreshToken = refreshToken;
      restoreTokens();
      var tokensChanged =
        previousAccessToken !== accessToken || previousRefreshToken !== refreshToken;
      if (!tokensChanged && !forceRefresh) return;

      refreshSession().finally(function () {
        setAuthReady(true);
        renderAttributes();
      });
    }, 25);
  }

  function clearTokens() {
    accessToken = null;
    refreshToken = null;
    session = null;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
    paymentMethodDataPromise = null;
    paymentMethodDataKey = null;
    membershipDataPromise = null;
    membershipDataToken = null;
    guestFlowDataPromise = null;
    guestFlowDataToken = null;
    safeStorage(window.localStorage, storagePrefix + "access_token", null);
    safeStorage(window.localStorage, storagePrefix + "refresh_token", null);
  }

  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    if (!session || !session.expiresAt || !refreshToken) return;
    var delay = session.expiresAt - Date.now() - 5 * 60 * 1000;
    refreshTimer = setTimeout(refreshTokenIfNeeded, Math.max(0, delay));
  }

  function initWithTokens(nextAccessToken, nextRefreshToken) {
    accessToken = nextAccessToken || null;
    refreshToken = nextRefreshToken || null;
    saveTokens();
    return refreshSession();
  }

  function onAuthStateChange(callback) {
    if (typeof callback !== "function") return function () {};
    listeners.push(callback);
    callback(session);
    return function () {
      listeners = listeners.filter(function (listener) {
        return listener !== callback;
      });
    };
  }

  function notifyAuthState() {
    if (accessToken && accessToken !== lastIframePingAccessToken) {
      lastIframePingAccessToken = accessToken;
      pingRegisteredIframes();
    } else if (!accessToken) {
      lastIframePingAccessToken = null;
    }
    listeners.forEach(function (listener) {
      try {
        listener(session);
      } catch (error) {
        log("Auth listener failed", error);
      }
    });
  }

  function renderAttributes() {
    bindLogoutButtons();
    bindPasswordlessForms();
    bindProfileForms();
    bindEnquiryForms();
    bindMarketingSubscribeForms();
    renderAuthVisibility();
    renderCustomerFields();
    renderCustomerParameters();
    renderWalletCardLinks();
    renderOrderLists();
    renderBookingLists();
    renderMembershipLists();
    renderMembershipPlanLists();
    renderGuestFlowLists();
    renderGuestPassLists();
    renderPassBalances();
    renderFlowServiceLists();
    renderFlowServiceDetails();
    renderBusinessPolicies();
    renderPaymentMethodLists();
    mountPaymentMethodEmbeds();
    bindPhoneNumberFields();
  }

  function forceRenderAttributes() {
    guestPassRevision += 1;
    selectAll("[data-tiquo-policy]").forEach(function (container) {
      container.__tiquoPolicyId = null;
    });
    selectAll("[data-tiquo-orders]").forEach(function (container) {
      container.__tiquoRenderedOrdersFor = null;
    });
    selectAll("[data-tiquo-bookings]").forEach(function (container) {
      container.__tiquoRenderedBookingsFor = null;
    });
    selectAll("[data-tiquo-memberships]").forEach(function (container) {
      container.__tiquoRenderedMembershipsFor = null;
    });
    selectAll("[data-tiquo-membership-plans]").forEach(function (container) {
      container.__tiquoRenderedMembershipPlansFor = null;
    });
    selectAll("[data-tiquo-guest-flows]").forEach(function (container) {
      container.__tiquoRenderedGuestFlowsFor = null;
    });
    selectAll("[data-tiquo-services]").forEach(function (container) {
      container.__tiquoRenderedServicesFor = null;
    });
    selectAll("[data-tiquo-service-detail]").forEach(function (container) {
      container.__tiquoRenderedServiceDetailFor = null;
    });
    invalidatePaymentMethods();
    membershipDataPromise = null;
    membershipDataToken = null;
    guestFlowDataPromise = null;
    guestFlowDataToken = null;
    flowServiceDataPromises = {};
    renderAttributes();
  }

  function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    setTimeout(function () {
      renderScheduled = false;
      renderAttributes();
    }, 50);
  }

  function observeDom() {
    if (!window.MutationObserver || mutationObserver) return;
    mutationObserver = new MutationObserver(scheduleRender);
    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function bindLogoutButtons() {
    selectAll("[data-tiquo-logout]").forEach(function (button) {
      if (button.__tiquoLogoutBound) return;
      button.__tiquoLogoutBound = true;
      button.addEventListener("click", function (event) {
        event.preventDefault();
        if (button.__tiquoLogoutLoading) return;
        button.__tiquoLogoutLoading = true;
        setLogoutLoading(button, true);
        logout()
          .then(function () {
            var redirectTo = button.getAttribute("data-tiquo-logout-redirect");
            if (redirectTo) window.location.href = redirectTo;
          })
          .finally(function () {
            button.__tiquoLogoutLoading = false;
            setLogoutLoading(button, false);
          });
      });
    });
  }

  function setLogoutLoading(button, loading) {
    if (!button) return;
    if (button.__tiquoLogoutOriginalText === undefined) {
      button.__tiquoLogoutOriginalText = button.textContent;
    }
    var loadingText = button.getAttribute("data-tiquo-logout-loading");
    if (loadingText) {
      button.textContent = loading ? loadingText : button.__tiquoLogoutOriginalText;
    }
    if ("disabled" in button) button.disabled = loading;
    button.setAttribute("aria-busy", loading ? "true" : "false");
    button.setAttribute("aria-disabled", loading ? "true" : "false");
  }

  function bindPasswordlessForms() {
    selectAll("[data-tiquo-form='passwordless-login'],[data-tiquo-form='passwordless-signup']").forEach(function (form) {
      if (form.__tiquoBound) return;
      form.__tiquoBound = true;
      form.setAttribute("novalidate", "novalidate");
      preservePasswordlessFormState(form);
      showStepOne(form);
      hideStepTwo(form);
      form.addEventListener("submit", function (event) {
        handlePasswordlessSubmit(form, event);
      }, true);
      selectAll("[type='submit'],button:not([type])", form).forEach(function (button) {
        button.addEventListener("click", function (event) {
          handlePasswordlessSubmit(form, event);
        }, true);
      });
      form.addEventListener("keydown", function (event) {
        if (event.key === "Enter") handlePasswordlessSubmit(form, event);
      }, true);
    });
  }

  function handlePasswordlessSubmit(form, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    }
    if (form.__tiquoSubmitting) return false;

    preservePasswordlessFormState(form);
    var emailInput = form.querySelector("[data-tiquo-member='email']");
    var tokenInput = form.querySelector("[data-tiquo-member='token']");
    var button = form.querySelector("[type='submit']") || form.querySelector("button");
    var email = emailInput && emailInput.value ? emailInput.value.trim() : "";
    var token = tokenInput && tokenInput.value ? tokenInput.value.trim() : "";
    if (!email) {
      var error = new Error("Enter your email address.");
      setFormMessage(form, error.message);
      dispatchProfileFormEvent(form, "tiquo:auth-error", { error: error, phase: token ? "verify" : "send" });
      return false;
    }

    form.__tiquoSubmitting = true;
    var passwordlessPhase = token ? "verify" : "send";
    setLoading(button, true, form, passwordlessPhase);
    var action = token
      ? verifyOTP(email, token)
      : sendOTP(email).then(function (result) {
          hideStepOne(form);
          showStepTwo(form);
          updatePasswordlessButton(form, button);
          preservePasswordlessFormState(form);
          return result;
        });
    action
      .then(function (result) {
        setFormMessage(form, token ? "Signed in." : "Check your email for the code.");
        dispatchProfileFormEvent(form, token ? "tiquo:authenticated" : "tiquo:otp-sent",
          token ? { session: session } : result);
        preservePasswordlessFormState(form);
        renderAttributes();
      })
      .catch(function (error) {
        setFormMessage(form, error.message || "Something went wrong.");
        dispatchProfileFormEvent(form, "tiquo:auth-error", { error: error, phase: passwordlessPhase });
        preservePasswordlessFormState(form);
      })
      .finally(function () {
        form.__tiquoSubmitting = false;
        setLoading(button, false, form, passwordlessPhase);
      });
    return false;
  }

  function collectEnquiryFormPayload(form) {
    var customer = {};
    var enquiry = {};
    selectAll("[data-tiquo-enquiry-customer]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      customer[field.getAttribute("data-tiquo-enquiry-customer")] = getFieldValue(field);
    });
    selectAll("[data-tiquo-enquiry]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      enquiry[field.getAttribute("data-tiquo-enquiry")] = getFieldValue(field);
    });
    var parameters = {};
    var checkboxes = {};
    selectAll("[data-tiquo-enquiry-parameter]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      var id = field.getAttribute("data-tiquo-enquiry-parameter");
      if (!id) return;
      if (field.type === "checkbox") {
        checkboxes[id] = checkboxes[id] || [];
        checkboxes[id].push(field);
      } else parameters[id] = getFieldValue(field);
    });
    Object.keys(checkboxes).forEach(function (id) {
      var fields = checkboxes[id];
      parameters[id] = fields.length === 1 ? !!fields[0].checked : fields.filter(function (field) {
        return field.checked;
      }).map(function (field) { return field.value; });
    });
    if (Object.keys(parameters).length) enquiry.enquiryParameterValues = Object.keys(parameters).map(function (id) {
      return { enquiryParameterId: id, value: parameters[id] };
    });
    var parameters = {};
    var checkboxes = {};
    selectAll("[data-tiquo-enquiry-customer-parameter]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      var id = field.getAttribute("data-tiquo-enquiry-customer-parameter");
      if (!id) return;
      if (field.type === "checkbox") {
        checkboxes[id] = checkboxes[id] || [];
        checkboxes[id].push(field);
      } else parameters[id] = getFieldValue(field);
    });
    Object.keys(checkboxes).forEach(function (id) {
      var fields = checkboxes[id];
      parameters[id] = fields.length === 1 ? !!fields[0].checked : fields.filter(function (field) {
        return field.checked;
      }).map(function (field) { return field.value; });
    });
    if (Object.keys(parameters).length) customer.customerParameterValues = Object.keys(parameters).map(function (id) {
      return { customerParameterId: id, value: parameters[id] };
    });
    applyFormPhoneField(customer, form, "data-tiquo-enquiry-customer");
    return { customer: customer, enquiry: enquiry };
  }

  function bindEnquiryForms() {
    selectAll("[data-tiquo-form='enquiry']").forEach(function (form) {
      if (form.__tiquoEnquiryBound) return;
      form.__tiquoEnquiryBound = true;
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        if (form.__tiquoSubmitting) return;
        form.__tiquoSubmitting = true;
        var button = form.querySelector("[type='submit']") || form.querySelector("button");
        var payload = collectEnquiryFormPayload(form);
        setLoading(button, true);
        createEnquiry(payload).then(function (result) {
          setFormMessage(form, "Enquiry submitted.");
          dispatchProfileFormEvent(form, "tiquo:enquiry-created", result);
        }).catch(function (error) {
          setFormMessage(form, error.message || "Failed to create enquiry.");
          dispatchProfileFormEvent(form, "tiquo:enquiry-error", { error: error });
        }).finally(function () {
          form.__tiquoSubmitting = false;
          setLoading(button, false);
        });
      });
    });
  }

  function collectMarketingSubscribeFormPayload(form) {
    var payload = {};
    var fields = ["email", "firstName", "lastName", "phone", "phoneCountry", "emailOptIn", "smsOptIn", "honeypot"];
    selectAll("[data-tiquo-marketing]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      var key = field.getAttribute("data-tiquo-marketing");
      if (fields.indexOf(key) === -1) return;
      var value = getFieldValue(field);
      payload[key] = key === "emailOptIn" || key === "smsOptIn"
        ? value === true || value === "true"
        : value;
    });
    applyFormPhoneField(payload, form, "data-tiquo-marketing");
    return payload;
  }

  function bindMarketingSubscribeForms() {
    selectAll("[data-tiquo-form='marketing-subscribe']").forEach(function (form) {
      if (form.__tiquoMarketingSubscribeBound) return;
      form.__tiquoMarketingSubscribeBound = true;
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        if (form.__tiquoSubmitting) return;
        form.__tiquoSubmitting = true;
        var button = form.querySelector("[type='submit']") || form.querySelector("button");
        var payload = collectMarketingSubscribeFormPayload(form);
        setFormMessage(form, "");
        setLoading(button, true);
        subscribeToMarketing(payload).then(function (result) {
          setFormMessage(form, "Thanks — your request has been received.");
          dispatchProfileFormEvent(form, "tiquo:marketing-subscribed", result);
        }).catch(function (error) {
          setFormMessage(form, error.message || "Failed to subscribe to marketing.");
          dispatchProfileFormEvent(form, "tiquo:marketing-error", { error: error });
        }).finally(function () {
          form.__tiquoSubmitting = false;
          setLoading(button, false);
        });
      });
    });
  }

  function bindProfileForms() {
    selectAll("[data-tiquo-form='profile']").forEach(function (form) {
      populateProfileForm(form);
      bindProfilePhotoTrigger(form);
      if (form.__tiquoProfileBound) return;
      form.__tiquoProfileBound = true;
      form.addEventListener("click", function (event) {
        if (!form.getAttribute("data-tiquo-profile-editor")) return;
        var target = event.target;
        var submitButton = target && target.closest
          ? target.closest("[type='submit']")
          : null;
        if (submitButton && form.contains(submitButton)) event.stopPropagation();
      }, true);
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        if (form.__tiquoSubmitting) return;
        if (!session) {
          var error = new Error("Sign in to update your profile.");
          setFormMessage(form, error.message);
          dispatchProfileFormEvent(form, "tiquo:profile-error", { error: error });
          return;
        }
        var button = form.querySelector("[type='submit']") || form.querySelector("button");
        var payload;
        try {
          payload = collectProfileFormPayload(form);
        } catch (error) {
          setFormMessage(form, error.message || "Check the profile fields.");
          dispatchProfileFormEvent(form, "tiquo:profile-error", { error: error });
          return;
        }
        form.__tiquoSubmitting = true;
        setFormMessage(form, "");
        setLoading(button, true);
        updateProfile(payload)
          .then(function (result) {
            setFormMessage(form, "Profile saved.");
            clearProfilePhotoSelection(form);
            dispatchProfileFormEvent(form, "tiquo:profile-saved", result);
            closeProfileEditor(form);
          })
          .catch(function (error) {
            setFormMessage(form, error.message || "Failed to save profile.");
            dispatchProfileFormEvent(form, "tiquo:profile-error", {
              error: error,
            });
          })
          .finally(function () {
            form.__tiquoSubmitting = false;
            setLoading(button, false);
          });
      });
    });
  }

  function populateProfileForm(form) {
    if (!session || !session.customer) return;
    selectAll("[data-tiquo-profile]", form).forEach(function (field) {
      var name = field.getAttribute("data-tiquo-profile");
      if (name === "displayName") return;
      if (name === "profilePhoto" && field.type === "file") return;
      var value = getPath(session.customer, name === "email" ? "email" : name);
      // The primary country selector is input-only; preserve its configured default.
      if ((name === "phoneCountry" || name === "portalSlug" || /\.countryCode$/.test(name)) &&
          (value === undefined || value === "")) return;
      // No saved value (such as a new address row) restores the markup default,
      // so hidden defaults like addresses.1.type="billing" survive the fill.
      if (!hasPathValue(session.customer, name)) {
        resetFieldToDefault(field);
      } else {
        setFieldValue(field, value);
      }
      field.__tiquoProfileValue = profileFieldSnapshot(field);
    });
    selectAll("[data-tiquo-profile-parameter]", form).forEach(function (field) {
      var parameter = field.getAttribute("data-tiquo-profile-parameter");
      var value = getCustomerParameterValue(session.customer, parameter);
      if (field.type === "checkbox" && isConsentProfileParameter(session.customer, parameter)) {
        field.checked = parseConsentCheckboxValue(value);
      } else {
        setFieldValue(field, value);
      }
    });
  }

  function isConsentProfileParameter(customer, parameter) {
    function isConsentKey(value) {
      var key = normalizeParameterKey(value);
      return key === "system_marketing_opt_in" || key === "marketing_opt_in" ||
        key === "system_sms_opt_in" || key === "sms_opt_in";
    }
    if (isConsentKey(parameter)) return true;
    // Organization overrides may be addressed by their schema ID.
    return (customer.parameters || []).some(function (item) {
      return item.id === parameter && (isConsentKey(item.name) || isConsentKey(item.key));
    });
  }

  function parseConsentCheckboxValue(value) {
    if (typeof value === "string") {
      try { value = JSON.parse(value); } catch { /* Legacy plain values. */ }
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      value = value["Opt-In"] ?? value["Opted In"] ?? value.optedIn ?? value.optIn;
    }
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    return typeof value === "string" &&
      ["true", "1", "yes", "y", "on", "checked"].indexOf(value.trim().toLowerCase()) >= 0;
  }

  function collectProfileFormPayload(form) {
    var payload = {};
    var parameters = {};
    var primaryEmail;
    selectAll("[data-tiquo-profile]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      var name = field.getAttribute("data-tiquo-profile");
      if (!name) return;
      if (name === "displayName") {
        throw new Error("Display name cannot be edited from this form.");
      }
      if (name === "profilePhoto" && field.type === "file") {
        var photo = field.files && field.files[0];
        if (photo) payload.profilePhoto = photo;
        return;
      }
      var value = getFieldValue(field);
      if (name === "email") {
        var email = String(value || "").trim();
        if (email) primaryEmail = email;
        return;
      }
      var address = /^addresses\.(\d+)\.(id|type|isDefault|label|company|addressLine1|addressLine2|city|state|postalCode|country)$/.exec(name);
      if (address) {
        var addressIndex = Number(address[1]);
        if (addressIndex > 9) throw new Error("Profile forms support up to 10 addresses.");
        var addressKey = address[2];
        payload.addresses = payload.addresses || [];
        payload.addresses[addressIndex] = payload.addresses[addressIndex] || { type: "both", isDefault: addressIndex === 0 };
        payload.addresses[addressIndex][addressKey] = addressKey === "isDefault"
          ? value === true || value === "true"
          : value;
        return;
      }
      var contact = /^(emails|phones)\.(\d+)\.(address|number|countryCode|isPrimary|order)$/.exec(name);
      if (contact) {
        var list = contact[1];
        var index = Number(contact[2]);
        var key = contact[3];
        if (index > 99 || (list === "emails" && (key === "number" || key === "countryCode")) ||
            (list === "phones" && key === "address")) return;
        payload[list] = payload[list] || [];
        payload[list][index] = payload[list][index] || { isPrimary: index === 0, order: index };
        if (key === "isPrimary") value = value === true || value === "true";
        if (key === "order") {
          value = Number(value);
          if (!Number.isInteger(value) || value < 0) throw new Error("Contact order must be a non-negative integer.");
        }
        payload[list][index][key] = value;
        if (key === "number" && field.__tiquoPhoneFieldController) {
          var phoneValue = field.__tiquoPhoneFieldController.getValue();
          if (phoneValue.e164) payload[list][index].number = phoneValue.e164;
          if (phoneValue.countryCode) payload[list][index].countryCode = phoneValue.countryCode;
        }
        return;
      }
      if (name === "firstName" || name === "lastName" || name === "phone" || name === "phoneCountry" || name === "profilePhoto" || name === "portalSlug" || name === "taxId" ||
          name === "title" || name === "customTitle" || name === "nickname") {
        if (name === "phone") {
          var controller = field.__tiquoPhoneFieldController;
          var phoneValue = controller && controller.getValue
            ? controller.getValue()
            : null;
          payload.phone = phoneValue && phoneValue.e164
            ? phoneValue.e164
            : /^\s*(?:\+|00)/.test(String(value || ""))
              ? normalizePhoneNumber(String(value || ""))
              : String(value || "").replace(/\D/g, "");
          if (phoneValue && phoneValue.countryCode) {
            payload.phoneCountry = phoneValue.countryCode;
          }
        } else {
          payload[name] = value;
        }
      }
    });

    if (primaryEmail) {
      if (payload.emails) throw new Error("Use either email or indexed emails fields in a profile form.");
      payload.emails = [{ address: primaryEmail, isPrimary: true, order: 0 }];
    }

    ["emails", "phones", "addresses"].forEach(function (list) {
      if (payload[list]) payload[list] = payload[list].filter(function (contact) { return !!contact; });
    });

    // An empty Tax ID clears it, and a title select without an option for the
    // saved title (such as "other") reads as empty. Send them only when edited.
    if (!profileFieldsChanged(form, ["taxId"])) delete payload.taxId;
    if (!profileFieldsChanged(form, ["title", "customTitle"])) {
      delete payload.title;
      delete payload.customTitle;
    }

    var groupedCheckboxes = {};
    selectAll("[data-tiquo-profile-parameter]", form).forEach(function (field) {
      if (!isSubmittableField(field)) return;
      var parameter = field.getAttribute("data-tiquo-profile-parameter");
      if (!parameter) return;
      if (field.type === "checkbox") {
        groupedCheckboxes[parameter] = groupedCheckboxes[parameter] || [];
        groupedCheckboxes[parameter].push(field);
        return;
      }
      parameters[parameter] = getFieldValue(field);
    });

    Object.keys(groupedCheckboxes).forEach(function (parameter) {
      var fields = groupedCheckboxes[parameter];
      if (fields.length === 1) {
        parameters[parameter] = !!fields[0].checked;
        return;
      }
      parameters[parameter] = fields
        .filter(function (field) { return field.checked; })
        .map(function (field) { return field.value; });
    });

    if (Object.keys(parameters).length > 0) payload.profileParameters = parameters;
    return payload;
  }

  function applyFormPhoneField(payload, form, attribute) {
    var field = selectAll("[" + attribute + "]", form).find(function (field) {
      return field.getAttribute(attribute) === "phone" && isSubmittableField(field);
    });
    if (!isSubmittableField(field)) return;
    var controller = field.__tiquoPhoneFieldController;
    if (!controller || !controller.getValue) return;
    var phone = controller.getValue();
    if (phone.e164) payload.phone = phone.e164;
    if (phone.countryCode && !payload.phoneCountry) payload.phoneCountry = phone.countryCode;
  }

  function bindProfilePhotoTrigger(form) {
    var root = getProfileEditor(form) || form;
    selectAll("[data-tiquo-profile-photo-trigger]", root).forEach(function (trigger) {
      if (trigger.__tiquoProfilePhotoBound) return;
      trigger.__tiquoProfilePhotoBound = true;

      var input = document.createElement("input");
      input.type = "file";
      input.accept = trigger.getAttribute("data-tiquo-accept") || ".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp";
      input.setAttribute("data-tiquo-profile", "profilePhoto");
      input.setAttribute("aria-hidden", "true");
      input.tabIndex = -1;
      input.style.display = "none";
      form.appendChild(input);

      if (!trigger.getAttribute("role")) trigger.setAttribute("role", "button");
      if (!trigger.getAttribute("tabindex")) trigger.setAttribute("tabindex", "0");

      function choosePhoto(event) {
        if (event) event.preventDefault();
        input.click();
      }

      trigger.addEventListener("click", choosePhoto);
      trigger.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") choosePhoto(event);
      });
      input.addEventListener("change", function () {
        var photo = input.files && input.files[0];
        if (!photo) return;
        var maxSize = Number(trigger.getAttribute("data-tiquo-max-size") || 5242880);
        if (["image/jpeg", "image/png", "image/gif", "image/webp"].indexOf((photo.type || "").toLowerCase()) === -1) {
          input.value = "";
          setFormMessage(form, "Only JPEG, PNG, GIF, and WebP images are supported.");
          return;
        }
        if (Number.isFinite(maxSize) && maxSize > 0 && photo.size > maxSize) {
          input.value = "";
          setFormMessage(form, "The selected image is too large.");
          return;
        }
        showProfilePhotoPreview(form, root, photo);
        setFormMessage(form, "");
      });
    });
  }

  function getProfileEditor(form) {
    var selector = form.getAttribute("data-tiquo-profile-editor");
    if (selector) {
      try {
        var target = document.querySelector(selector);
        if (target) return target;
      } catch {
        // Fall back to a containing editor.
      }
    }
    return form.closest ? form.closest("[data-tiquo-profile-editor]") : null;
  }

  function showProfilePhotoPreview(form, root, photo) {
    if (!window.URL || typeof window.URL.createObjectURL !== "function") return;
    if (form.__tiquoProfilePhotoPreviewUrl) {
      window.URL.revokeObjectURL(form.__tiquoProfilePhotoPreviewUrl);
    }
    var previewUrl = window.URL.createObjectURL(photo);
    form.__tiquoProfilePhotoPreviewUrl = previewUrl;
    selectAll("[data-tiquo-profile-photo-preview]", root).forEach(function (preview) {
      if (preview.tagName === "IMG") preview.setAttribute("src", previewUrl);
      else preview.style.backgroundImage = 'url("' + previewUrl.replace(/["\\]/g, "\\$&") + '")';
    });
  }

  function clearProfilePhotoSelection(form) {
    selectAll("input[type='file'][data-tiquo-profile='profilePhoto']", form).forEach(function (input) {
      input.value = "";
    });
    if (form.__tiquoProfilePhotoPreviewUrl && window.URL) {
      window.URL.revokeObjectURL(form.__tiquoProfilePhotoPreviewUrl);
      form.__tiquoProfilePhotoPreviewUrl = null;
    }
  }

  function closeProfileEditor(form) {
    var editor = getProfileEditor(form);
    if (editor) editor.style.display = "none";
  }

  function dispatchProfileFormEvent(form, name, detail) {
    if (!form.dispatchEvent || typeof CustomEvent === "undefined") return;
    form.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: detail,
    }));
  }

  function isSubmittableField(field) {
    if (!field || field.disabled || (field.matches && field.matches(":disabled"))) return false;
    var parent = field.parentElement;
    while (parent) {
      if (parent.tagName === "FIELDSET" && parent.disabled) {
        var legend = Array.prototype.find.call(parent.children, function (child) { return child.tagName === "LEGEND"; });
        if (!legend || !legend.contains(field)) return false;
      }
      parent = parent.parentElement;
    }
    if (field && field.type === "radio" && !field.checked) return false;
    return true;
  }

  function getFieldValue(field) {
    if (field.tagName === "SELECT" && field.multiple) {
      return Array.prototype.slice.call(field.options)
        .filter(function (option) {
          return option.selected && !option.disabled &&
            !(option.parentNode && option.parentNode.tagName === "OPTGROUP" && option.parentNode.disabled);
        })
        .map(function (option) { return option.value; });
    }
    if (field.type === "checkbox") return !!field.checked;
    if (field.type === "radio") return field.checked ? field.value : "";
    return field.value !== undefined ? field.value : "";
  }

  function resetFieldToDefault(field) {
    if (field.tagName === "SELECT") {
      Array.prototype.slice.call(field.options).forEach(function (option) {
        option.selected = option.defaultSelected;
      });
      return;
    }
    if (field.type === "checkbox" || field.type === "radio") {
      field.checked = field.defaultChecked;
      return;
    }
    if (field.defaultValue !== undefined) field.value = field.defaultValue;
  }

  function profileFieldSnapshot(field) {
    return JSON.stringify(getFieldValue(field));
  }

  // Fields never filled from a profile count as changed, so they are sent.
  function profileFieldsChanged(form, names) {
    return selectAll("[data-tiquo-profile]", form).some(function (field) {
      if (names.indexOf(field.getAttribute("data-tiquo-profile")) < 0) return false;
      return field.__tiquoProfileValue === undefined ||
        field.__tiquoProfileValue !== profileFieldSnapshot(field);
    });
  }

  function setFieldValue(field, value) {
    if (field.tagName === "SELECT" && field.multiple) {
      var values = Array.isArray(value) ? value : parseMultiselectValue(value);
      Array.prototype.slice.call(field.options).forEach(function (option) {
        option.selected = values.indexOf(option.value) >= 0;
      });
      return;
    }
    if (field.type === "checkbox") {
      if (typeof value === "boolean") {
        field.checked = value;
        return;
      }
      if (field.value && field.value !== "on") {
        var checkboxValues = Array.isArray(value) ? value : parseMultiselectValue(value);
        field.checked = checkboxValues.indexOf(field.value) >= 0;
      } else {
        field.checked = value === true || value === "true" || value === "1";
      }
      return;
    }
    if (field.type === "radio") {
      field.checked = String(value || "") === field.value;
      return;
    }
    if (field.value !== undefined) field.value = value === undefined || value === null ? "" : String(value);
  }

  function parseMultiselectValue(value) {
    if (Array.isArray(value)) return value.map(String);
    if (!value) return [];
    if (typeof value === "string") {
      try {
        var parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        return value.split(",").map(function (entry) { return entry.trim(); }).filter(Boolean);
      }
    }
    return [String(value)];
  }

  function hideStepTwo(root) {
    selectAll("[data-tiquo-passwordless='step-2']", root).forEach(function (element) {
      element.hidden = true;
      setStepControlsDisabled(element, true);
    });
  }

  function showStepTwo(root) {
    selectAll("[data-tiquo-passwordless='step-2']", root).forEach(function (element) {
      element.hidden = false;
      setStepControlsDisabled(element, false);
    });
  }

  function hideStepOne(root) {
    selectAll("[data-tiquo-passwordless='step-1']", root).forEach(function (element) {
      element.hidden = true;
      setStepControlsDisabled(element, true);
    });
  }

  function showStepOne(root) {
    selectAll("[data-tiquo-passwordless='step-1']", root).forEach(function (element) {
      element.hidden = false;
      setStepControlsDisabled(element, false);
    });
  }

  function setStepControlsDisabled(root, disabled) {
    selectAll("input, select, textarea", root).forEach(function (control) {
      if (disabled) {
        control.__tiquoWasDisabled = control.disabled;
        control.disabled = true;
        return;
      }
      control.disabled = Boolean(control.__tiquoWasDisabled);
      delete control.__tiquoWasDisabled;
    });
  }

  function updatePasswordlessButton(form, button) {
    if (!button) return;
    var text = form.getAttribute("data-tiquo-passwordless-button") ||
      button.getAttribute("data-tiquo-passwordless-button");
    if (!text) return;
    setButtonText(button, text);
  }

  function getPasswordlessLoadingText(form, button, phase) {
    if (!button) return "";
    var specificName = phase === "verify"
      ? "data-tiquo-passwordless-verify-loading"
      : "data-tiquo-passwordless-send-loading";
    return form.getAttribute(specificName) ||
      button.getAttribute(specificName) ||
      form.getAttribute("data-tiquo-passwordless-loading") ||
      button.getAttribute("data-tiquo-passwordless-loading") ||
      "";
  }

  function getButtonText(button) {
    if (!button) return "";
    if (button.tagName === "INPUT") {
      return button.value || "";
    }
    return button.textContent || "";
  }

  function setButtonText(button, text) {
    if (!button) return;
    if (button.tagName === "INPUT") {
      button.value = text;
      return;
    }
    button.textContent = text;
  }

  function preservePasswordlessFormState(form) {
    restorePasswordlessFormState(form);
    [0, 50, 250, 1000].forEach(function (delay) {
      setTimeout(function () {
        restorePasswordlessFormState(form);
      }, delay);
    });
  }

  function restorePasswordlessFormState(form) {
    var showPasswordlessForm = authReady && !isAuthenticated();
    form.hidden = !showPasswordlessForm;
    form.style.display = showPasswordlessForm ? "" : "none";
    var wrapper = form.closest ? form.closest(".w-form") : null;
    if (!wrapper) return;
    selectAll(".w-form-done,.w-form-fail", wrapper).forEach(function (element) {
      element.hidden = true;
      element.style.display = "none";
    });
  }

  function setLoading(button, loading, form, phase) {
    if (!button) return;
    var loadingText = form && phase ? getPasswordlessLoadingText(form, button, phase) : "";
    if (loadingText) {
      if (loading) {
        button.__tiquoLoadingPreviousText = getButtonText(button);
        setButtonText(button, loadingText);
      } else {
        setButtonText(button, button.__tiquoLoadingPreviousText || getButtonText(button));
        delete button.__tiquoLoadingPreviousText;
      }
      if (!loading && phase === "send") updatePasswordlessButton(form, button);
    }
    button.disabled = loading;
    button.setAttribute("aria-busy", loading ? "true" : "false");
  }

  function setFormMessage(form, message) {
    var target = form.querySelector("[data-tiquo-message]");
    if (!target && message && document.createElement) {
      target = document.createElement("div");
      target.setAttribute("data-tiquo-message", "");
      target.setAttribute("aria-live", "polite");
      target.className = "tiquo-form-message";
      var submit = form.querySelector("[type='submit']");
      var actions = submit && submit.parentNode;
      if (actions && actions.parentNode && actions.parentNode.insertBefore) {
        actions.parentNode.insertBefore(target, actions);
      } else {
        form.appendChild(target);
      }
    }
    if (!target) return;
    target.textContent = message;
    target.hidden = !message;
  }

  function renderAuthVisibility() {
    var hasStoredSession = !!(session || accessToken || refreshToken);
    selectAll("[data-tiquo-show]").forEach(function (element) {
      var mode = element.getAttribute("data-tiquo-show");
      var show = authReady && (
        mode === "logged-in"
          ? !!session
          : mode === "logged-out"
            ? !hasStoredSession
            : true
      );
      setElementVisible(element, show);
    });
    selectAll("[data-tiquo-form='passwordless-login'],[data-tiquo-form='passwordless-signup']").forEach(function (form) {
      var showPasswordlessForm = authReady && !hasStoredSession;
      form.hidden = !showPasswordlessForm;
      form.style.display = showPasswordlessForm ? "" : "none";
    });
  }

  function setElementVisible(element, visible) {
    if (!element) return;
    if (element.__tiquoOriginalDisplay === undefined) {
      element.__tiquoOriginalDisplay = element.style.display || "";
    }
    element.hidden = !visible;
    element.style.display = visible ? element.__tiquoOriginalDisplay : "none";
  }

  function renderCustomerFields() {
    selectAll("[data-tiquo-customer]").forEach(function (element) {
      var field = element.getAttribute("data-tiquo-customer");
      var value = "";
      if (session && session.customer) {
        value = /^parameterValues\./.test(field)
          ? getCustomerParameterValue(session.customer, field.slice("parameterValues.".length))
          : getPath(session.customer, field);
      }
      var formattedValue = formatCustomerFieldValue(element, field, value);
      var hideWhenEmpty = element.getAttribute("data-tiquo-hide-empty") !== null;
      var styleBinding = element.getAttribute("data-tiquo-style");
      if (hideWhenEmpty) setElementVisible(element, Boolean(formattedValue));
      if (element.tagName === "IMG") {
        setSafeImageSource(element, formattedValue);
      } else if (styleBinding) {
        setTiquoStyle(element, styleBinding, formattedValue);
      } else {
        element.textContent = formattedValue;
      }
    });
  }

  function renderCustomerParameters() {
    selectAll("[data-tiquo-customer-parameter]").forEach(function (element) {
      var parameter = element.getAttribute("data-tiquo-customer-parameter");
      var value = session && session.customer ? getCustomerParameterValue(session.customer, parameter) : "";
      element.textContent = formatValue(value);
    });
  }

  function renderWalletCardLinks() {
    selectAll("[data-tiquo-wallet-card]").forEach(function (element) {
      var designName = String(element.getAttribute("data-tiquo-wallet-card") || "").trim();
      var walletType = normalizeWalletCardType(element.getAttribute("data-tiquo-wallet-type"));
      var renderKey = [
        session && session.customer ? session.customer.id : "logged-out",
        designName,
        walletType,
      ].join("|");

      if (element.__tiquoWalletCardRenderedFor === renderKey) return;
      element.__tiquoWalletCardRenderedFor = renderKey;

      if (!session || !session.customer || !designName) {
        setWalletCardElementUrl(element, "");
        return;
      }

      var cacheKey = session.customer.id + "|" + designName;
      var cachedLinks = walletCardLinksCache[cacheKey];
      if (cachedLinks) {
        setWalletCardElementUrl(element, getWalletCardUrl(cachedLinks, walletType));
        return;
      }

      setWalletCardElementLoading(element, true);
      getWalletCardLinks({ designName: designName })
        .then(function (links) {
          walletCardLinksCache[cacheKey] = links;
          setWalletCardElementUrl(element, getWalletCardUrl(links, walletType));
        })
        .catch(function (error) {
          log("Failed to render wallet card link", error);
          setWalletCardElementUrl(element, "");
        })
        .finally(function () {
          setWalletCardElementLoading(element, false);
        });
    });
  }

  function normalizeWalletCardType(value) {
    var type = String(value || "").trim().toLowerCase();
    return type === "apple" || type === "google" ? type : "generic";
  }

  function getWalletCardUrl(links, walletType) {
    if (!links) return "";
    if (walletType === "apple") return links.appleWalletUrl || links.walletCardUrl || "";
    if (walletType === "google") return links.googleWalletUrl || links.walletCardUrl || "";
    return links.walletCardUrl || "";
  }

  function setWalletCardElementUrl(element, url) {
    element.__tiquoWalletCardUrl = url || "";
    if (element.tagName === "A") {
      if (url) setSafeHref(element, url);
      else element.removeAttribute("href");
    }
    element.setAttribute("aria-disabled", url ? "false" : "true");
    if (element.__tiquoWalletCardClickBound) return;
    element.__tiquoWalletCardClickBound = true;
    element.addEventListener("click", function (event) {
      var nextUrl = element.__tiquoWalletCardUrl;
      if (!nextUrl) {
        event.preventDefault();
        return;
      }
      if (element.tagName !== "A") {
        event.preventDefault();
        window.location.href = nextUrl;
      }
    });
  }

  function setWalletCardElementLoading(element, loading) {
    var loadingText = element.getAttribute("data-tiquo-wallet-card-loading");
    if (loadingText) {
      if (element.__tiquoWalletCardOriginalText === undefined) {
        element.__tiquoWalletCardOriginalText = element.textContent;
      }
      element.textContent = loading ? loadingText : element.__tiquoWalletCardOriginalText;
    }
    if ("disabled" in element) element.disabled = loading;
    element.setAttribute("aria-busy", loading ? "true" : "false");
  }

  function renderOrderLists() {
    selectAll("[data-tiquo-orders]").forEach(function (container) {
      if (container.__tiquoLoadingOrders) return;
      var template = getTemplate(container, "[data-tiquo-order-item]");
      if (!template) return;
      var renderKey = [
        session && session.user ? session.user.id : "logged-out",
        container.getAttribute("data-tiquo-limit") || 10,
        container.getAttribute("data-tiquo-status") || "",
      ].join("|");
      if (container.__tiquoRenderedOrdersFor === renderKey) return;
      if (!session) {
        container.__tiquoRenderedOrdersFor = renderKey;
        renderEmptyList(container, template);
        return;
      }
      container.__tiquoLoadingOrders = true;
      getOrders({
        limit: container.getAttribute("data-tiquo-limit") || 10,
        status: container.getAttribute("data-tiquo-status") || undefined,
      })
        .then(function (result) {
          container.__tiquoRenderedOrdersFor = renderKey;
          renderList(container, template, result.orders || [], "[data-tiquo-order]", "data-tiquo-order", formatOrderField);
        })
        .catch(function (error) {
          log("Failed to render orders", error);
        })
        .finally(function () {
          container.__tiquoLoadingOrders = false;
        });
    });
  }

  function renderBookingLists() {
    selectAll("[data-tiquo-bookings]").forEach(function (container) {
      if (container.__tiquoLoadingBookings) return;
      var template = getTemplate(container, "[data-tiquo-booking-item]");
      if (!template) return;
      var bookingListOptions = getBookingListOptions(container);
      var renderKey = [
        session && session.user ? session.user.id : "logged-out",
        bookingListOptions.limit,
        bookingListOptions.range || "",
        container.getAttribute("data-tiquo-status") || "",
        container.getAttribute("data-tiquo-upcoming") || "",
      ].join("|");
      if (container.__tiquoRenderedBookingsFor === renderKey) return;
      if (!session) {
        container.__tiquoRenderedBookingsFor = renderKey;
        renderEmptyList(container, template);
        return;
      }
      container.__tiquoLoadingBookings = true;
      getBookings({
        limit: bookingListOptions.limit,
        status: container.getAttribute("data-tiquo-status") || undefined,
        range: bookingListOptions.range,
        upcoming: bookingListOptions.range ? undefined : readBoolean(container.getAttribute("data-tiquo-upcoming"), false),
      })
        .then(function (result) {
          container.__tiquoRenderedBookingsFor = renderKey;
          renderList(
            container,
            template,
            (result.bookings || []).map(normalizeBookingForRender),
            "[data-tiquo-booking]",
            "data-tiquo-booking",
            formatBookingField
          );
        })
        .catch(function (error) {
          log("Failed to render bookings", error);
        })
        .finally(function () {
          container.__tiquoLoadingBookings = false;
        });
    });
  }

  function renderMembershipLists() {
    selectAll("[data-tiquo-memberships]").forEach(function (container) {
      if (container.__tiquoLoadingMemberships) return;
      var template = getTemplate(container, "[data-tiquo-membership-item]");
      if (!template) return;
      var status = String(container.getAttribute("data-tiquo-status") || "active")
        .trim()
        .toLowerCase();
      var source = String(container.getAttribute("data-tiquo-source") || "all")
        .trim()
        .toLowerCase();
      var limit = readPositiveInteger(container.getAttribute("data-tiquo-limit"), 10);
      var renderKey = [
        session && session.user ? session.user.id : "logged-out",
        status,
        source,
        limit,
      ].join("|");
      if (container.__tiquoRenderedMembershipsFor === renderKey) return;
      if (!session) {
        container.__tiquoRenderedMembershipsFor = renderKey;
        renderEmptyList(container, template);
        return;
      }

      container.__tiquoLoadingMemberships = true;
      getMemberships()
        .then(function (result) {
          var memberships = (result.memberships || []).filter(function (membership) {
            return (
              (status === "all" || !status || String(membership.status || "").toLowerCase() === status) &&
              membershipMatchesSource(membership, source)
            );
          });
          container.__tiquoRenderedMembershipsFor = renderKey;
          renderList(
            container,
            template,
            memberships.slice(0, limit).map(normalizeMembershipForRender),
            "[data-tiquo-membership]",
            "data-tiquo-membership",
            formatMembershipField,
            function (node, membership) {
              // Lets a page hide Tiquo-only actions (change card, cancel) on
              // memberships managed in a connected system, using CSS alone.
              node.setAttribute("data-tiquo-membership-source", membership.source);
              node.setAttribute(
                "data-tiquo-membership-external",
                membership.isExternal ? "true" : "false",
              );
            },
          );
        })
        .catch(function (error) {
          log("Failed to render memberships", error);
        })
        .finally(function () {
          container.__tiquoLoadingMemberships = false;
        });
    });
  }

  function renderMembershipPlanLists() {
    selectAll("[data-tiquo-membership-plans]").forEach(function (container) {
      if (container.__tiquoLoadingMembershipPlans) return;
      var template = getTemplate(container, "[data-tiquo-membership-plan-item]");
      if (!template) return;
      var currentMode = String(container.getAttribute("data-tiquo-current") || "include")
        .trim()
        .toLowerCase();
      var limit = readPositiveInteger(container.getAttribute("data-tiquo-limit"), 50);
      var renderKey = [
        session && session.user ? session.user.id : "logged-out",
        currentMode,
        limit,
      ].join("|");
      if (container.__tiquoRenderedMembershipPlansFor === renderKey) return;
      if (!session) {
        container.__tiquoRenderedMembershipPlansFor = renderKey;
        renderEmptyList(container, template);
        return;
      }

      container.__tiquoLoadingMembershipPlans = true;
      getMemberships()
        .then(function (result) {
          var plans = (result.availablePlans || []).filter(function (plan) {
            if (currentMode === "only") return Boolean(plan.isCurrent);
            if (currentMode === "exclude") return !plan.isCurrent;
            return true;
          });
          container.__tiquoRenderedMembershipPlansFor = renderKey;
          renderList(
            container,
            template,
            plans.slice(0, limit).map(normalizeMembershipPlanForRender),
            "[data-tiquo-membership-plan]",
            "data-tiquo-membership-plan",
            formatMembershipPlanField,
          );
        })
        .catch(function (error) {
          log("Failed to render membership plans", error);
        })
        .finally(function () {
          container.__tiquoLoadingMembershipPlans = false;
        });
    });
  }

  function renderGuestPassLists() {
    selectAll("[data-tiquo-guest-passes]").forEach(function (container) {
      var template = getTemplate(container, "[data-tiquo-guest-pass-item]");
      if (!template) return;
      if (!session) {
        container.__tiquoRenderedGuestPassesFor = null;
        renderEmptyList(container, template);
        return;
      }
      if (container.__tiquoLoadingGuestPasses) return;
      var requestToken = accessToken;
      var revision = guestPassRevision;
      var limit = readPositiveInteger(container.getAttribute("data-tiquo-limit"), 20);
      var status = container.getAttribute("data-tiquo-guest-pass-status");
      var renderKey = [requestToken, revision, limit, status].join("|");
      if (container.__tiquoRenderedGuestPassesFor === renderKey) return;
      container.__tiquoLoadingGuestPasses = true;
      getGuestPasses().then(function (result) {
        if (!session || accessToken !== requestToken || guestPassRevision !== revision) return;
        container.__tiquoRenderedGuestPassesFor = renderKey;
        var passes = (result.guestPasses || []).filter(function (pass) {
          return !status || pass.status === status;
        }).slice(0, limit);
        renderList(container, template, passes, "[data-tiquo-guest-pass]", "data-tiquo-guest-pass", function (pass, field) {
          return getPath(pass, field);
        });
      }).catch(function (error) {
        log("Failed to render guest passes", error);
      }).finally(function () {
        container.__tiquoLoadingGuestPasses = false;
        if (session && (accessToken !== requestToken || guestPassRevision !== revision)) renderGuestPassLists();
      });
    });
  }

  // Pass balances, not invitations. Totals: data-tiquo-pass-balance-total =
  // "access" | "guest" | "guest-held". Lists: data-tiquo-pass-balances =
  // "access" | "guest", one data-tiquo-pass-balance-item per pass type.
  function renderPassBalances() {
    var totals = selectAll("[data-tiquo-pass-balance-total]");
    var lists = selectAll("[data-tiquo-pass-balances]").filter(function (container) {
      return Boolean(getTemplate(container, "[data-tiquo-pass-balance-item]"));
    });
    if (!totals.length && !lists.length) return;
    if (!session) {
      passBalanceRenderKey = null;
      totals.forEach(function (element) {
        element.__tiquoRenderedPassBalanceFor = null;
        element.textContent = "";
      });
      lists.forEach(function (container) {
        container.__tiquoRenderedPassBalanceFor = null;
        renderEmptyList(container, getTemplate(container, "[data-tiquo-pass-balance-item]"));
      });
      return;
    }
    var requestToken = accessToken;
    // Invitation writes and Tiquo.render() bump this, so held Guest Passes refresh too.
    var revision = guestPassRevision;
    var renderKey = requestToken + "|" + revision;
    var stale = totals.concat(lists).some(function (element) {
      return element.__tiquoRenderedPassBalanceFor !== renderKey;
    });
    if (!stale || passBalanceRenderKey === renderKey) return;
    passBalanceRenderKey = renderKey;
    getPassBalances().then(function (balances) {
      if (!session || accessToken !== requestToken || guestPassRevision !== revision) return;
      selectAll("[data-tiquo-pass-balance-total]").forEach(function (element) {
        element.__tiquoRenderedPassBalanceFor = renderKey;
        element.textContent = formatValue(
          getPassBalanceTotal(balances, element.getAttribute("data-tiquo-pass-balance-total")),
        );
      });
      selectAll("[data-tiquo-pass-balances]").forEach(function (container) {
        var template = getTemplate(container, "[data-tiquo-pass-balance-item]");
        if (!template) return;
        container.__tiquoRenderedPassBalanceFor = renderKey;
        var kind = String(container.getAttribute("data-tiquo-pass-balances") || "").trim().toLowerCase();
        var group = kind === "guest"
          ? balances.guestPassBalance
          : kind === "access"
            ? balances.accessPassBalance
            : null;
        renderList(
          container,
          template,
          (group && group.passTypes) || [],
          "[data-tiquo-pass-balance]",
          "data-tiquo-pass-balance",
          formatPassBalanceField,
        );
      });
    }).catch(function (error) {
      log("Failed to render pass balances", error);
    }).finally(function () {
      if (passBalanceRenderKey === renderKey) passBalanceRenderKey = null;
      if (session && (accessToken !== requestToken || guestPassRevision !== revision)) renderPassBalances();
    });
  }

  function getPassBalanceTotal(balances, kind) {
    var normalized = String(kind || "").trim().toLowerCase();
    if (normalized === "access") return balances.accessPassBalance ? balances.accessPassBalance.total : 0;
    if (normalized === "guest") return balances.guestPassBalance ? balances.guestPassBalance.total : 0;
    if (normalized === "guest-held") {
      return balances.guestPassBalance ? balances.guestPassBalance.heldByInvitations : 0;
    }
    return "";
  }

  function formatPassBalanceField(passType, field) {
    if (field === "nextExpiresAt") return formatDate(passType.nextExpiresAt);
    return getPath(passType, field);
  }

  function renderGuestFlowLists() {
    selectAll("[data-tiquo-guest-flows]").forEach(function (container) {
      if (container.__tiquoLoadingGuestFlows) return;
      var template = getTemplate(container, "[data-tiquo-guest-flow-item]");
      if (!template) return;
      var limit = readPositiveInteger(
        container.getAttribute("data-tiquo-limit"),
        20,
      );
      var renderKey = [
        session && session.user ? session.user.id : "logged-out",
        limit,
      ].join("|");
      if (container.__tiquoRenderedGuestFlowsFor === renderKey) return;
      if (!session) {
        container.__tiquoRenderedGuestFlowsFor = renderKey;
        renderEmptyList(container, template);
        return;
      }

      container.__tiquoLoadingGuestFlows = true;
      getGuestFlows()
        .then(function (result) {
          container.__tiquoRenderedGuestFlowsFor = renderKey;
          renderList(
            container,
            template,
            (result.guestFlows || [])
              .slice(0, limit)
              .map(normalizeGuestFlowForRender),
            "[data-tiquo-guest-flow]",
            "data-tiquo-guest-flow",
            formatGuestFlowField,
          );
        })
        .catch(function (error) {
          log("Failed to render guest flows", error);
        })
        .finally(function () {
          container.__tiquoLoadingGuestFlows = false;
        });
    });
  }

  function renderFlowServiceLists() {
    selectAll("[data-tiquo-services]").forEach(function (container) {
      if (container.__tiquoLoadingServices) return;
      var bookingConfigId = String(
        container.getAttribute("data-tiquo-services") || "",
      ).trim();
      var template = getTemplate(container, "[data-tiquo-service-item]");
      if (!bookingConfigId || !template) return;

      var limit = readPositiveInteger(
        container.getAttribute("data-tiquo-limit"),
        100,
      );
      var renderKey = [
        bookingConfigId,
        getFlowServiceAuthKey(),
        limit,
      ].join("|");
      if (container.__tiquoRenderedServicesFor === renderKey) return;

      container.__tiquoLoadingServices = true;
      getFlowServices(bookingConfigId)
        .then(function (catalog) {
          container.__tiquoRenderedServicesFor = renderKey;
          renderList(
            container,
            template,
            (catalog.services || [])
              .slice(0, limit)
              .map(function (service) {
                return normalizeFlowServiceForRender(
                  service,
                  bookingConfigId,
                  catalog.currency,
                  catalog.categories,
                );
              }),
            "[data-tiquo-service]",
            "data-tiquo-service",
            formatFlowServiceField,
            function (node, service) {
              bindFlowServiceGalleries(node, service);
              bindFlowServiceLinks(node, service, bookingConfigId);
              bindFlowServiceAccess(node, service);
            },
          );
        })
        .catch(function (error) {
          log("Failed to render customer flow services", error);
        })
        .finally(function () {
          container.__tiquoLoadingServices = false;
        });
    });
  }

  function getRequestedServiceId(container) {
    var configured = String(
      container.getAttribute("data-tiquo-service-id") || "",
    ).trim();
    if (configured) return configured;
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get("tiquoServiceId") || "";
    } catch {
      return "";
    }
  }

  function renderFlowServiceDetails() {
    selectAll("[data-tiquo-service-detail]").forEach(function (container) {
      if (container.__tiquoLoadingServiceDetail) return;
      var bookingConfigId = String(
        container.getAttribute("data-tiquo-service-detail") || "",
      ).trim();
      var serviceId = getRequestedServiceId(container);
      if (!bookingConfigId || !serviceId) return;

      var renderKey = [
        bookingConfigId,
        serviceId,
        getFlowServiceAuthKey(),
      ].join("|");
      if (container.__tiquoRenderedServiceDetailFor === renderKey) return;

      container.__tiquoLoadingServiceDetail = true;
      getFlowServices(bookingConfigId)
        .then(function (catalog) {
          var service = null;
          for (
            var index = 0;
            index < (catalog.services || []).length;
            index += 1
          ) {
            if (String(catalog.services[index].id) === serviceId) {
              service = normalizeFlowServiceForRender(
                catalog.services[index],
                bookingConfigId,
                catalog.currency,
                catalog.categories,
              );
              break;
            }
          }

          container.__tiquoRenderedServiceDetailFor = renderKey;
          var empty = container.querySelector("[data-tiquo-empty]");
          if (empty) empty.hidden = Boolean(service);
          if (!service) {
            bindFlowServiceFields(container, {});
            bindFlowServiceGalleries(container, {});
            bindFlowServiceAccess(container, {});
            selectIncludingRoot(
              "[data-tiquo-service-booking]",
              container,
            ).forEach(function (bookingContainer) {
              bookingContainer.__tiquoServiceBookingFor = null;
              bookingContainer.hidden = true;
              bookingContainer.innerHTML = "";
            });
            return;
          }

          bindFlowServiceFields(container, service);
          bindFlowServiceGalleries(container, service);
          bindFlowServiceLinks(container, service, bookingConfigId);
          bindFlowServiceAccess(container, service);
          selectIncludingRoot(
            "[data-tiquo-service-booking]",
            container,
          ).forEach(function (bookingContainer) {
            // A locked service can't be booked by this visitor, so don't
            // embed a booking form that would only say "Membership required".
            if (service.accessRestricted) {
              bookingContainer.__tiquoServiceBookingFor = null;
              bookingContainer.hidden = true;
              bookingContainer.innerHTML = "";
              return;
            }
            bookingContainer.hidden = false;
            var bookingKey = bookingConfigId + "|" + service.id;
            if (bookingContainer.__tiquoServiceBookingFor === bookingKey) {
              return;
            }
            bookingContainer.__tiquoServiceBookingFor = bookingKey;
            embedServiceBooking(
              bookingConfigId,
              service.id,
              bookingContainer,
            ).catch(function (error) {
              bookingContainer.__tiquoServiceBookingFor = null;
              log("Failed to embed service booking", error);
            });
          });
        })
        .catch(function (error) {
          log("Failed to render customer flow service", error);
        })
        .finally(function () {
          container.__tiquoLoadingServiceDetail = false;
        });
    });
  }

  function readPositiveInteger(value, fallback) {
    var parsed = parseInt(String(value || ""), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function getBookingListOptions(container) {
    var limitValue = String(container.getAttribute("data-tiquo-limit") || "").trim().toLowerCase();
    var range = limitValue === "upcoming" || limitValue === "past" ? limitValue : undefined;
    var countValue = container.getAttribute("data-tiquo-count") ||
      container.getAttribute("data-tiquo-page-size") ||
      (range ? "" : limitValue);
    var count = parseInt(countValue || "10", 10);
    return {
      limit: Number.isFinite(count) && count > 0 ? count : 10,
      range: range,
    };
  }

  function getTemplate(container, selector) {
    if (container.__tiquoTemplate) return container.__tiquoTemplate;
    var item = container.querySelector(selector);
    if (!item) return null;
    container.__tiquoTemplate = item.cloneNode(true);
    item.setAttribute("data-tiquo-rendered-template", "true");
    item.setAttribute("aria-hidden", "true");
    item.hidden = true;
    item.style.display = "none";
    return container.__tiquoTemplate;
  }

  function renderEmptyList(container, template) {
    clearRendered(container);
    var empty = container.querySelector("[data-tiquo-empty]");
    if (empty) empty.hidden = false;
    if (template && template.parentNode === container) {
      template.hidden = true;
      template.style.display = "none";
    }
  }

  function renderList(
    container,
    template,
    items,
    fieldSelector,
    fieldAttr,
    formatter,
    afterRender,
  ) {
    clearRendered(container);
    var empty = container.querySelector("[data-tiquo-empty]");
    if (empty) empty.hidden = items.length > 0;
    items.forEach(function (item) {
      var node = template.cloneNode(true);
      node.hidden = false;
      node.style.display = "";
      node.removeAttribute("aria-hidden");
      node.removeAttribute("data-tiquo-rendered-template");
      selectIncludingRoot(fieldSelector, node).forEach(function (fieldElement) {
        var field = fieldElement.getAttribute(fieldAttr);
        var value = formatter ? formatter(item, field) : getPath(item, field);
        var formattedValue = formatValue(imageBindingValue(fieldElement, value));
        var hideWhenEmpty = fieldElement.getAttribute("data-tiquo-hide-empty") !== null;
        var preserveText = fieldElement.getAttribute("data-tiquo-preserve-text") !== null;
        var styleBinding = fieldElement.getAttribute("data-tiquo-style");
        if (hideWhenEmpty) setElementVisible(fieldElement, Boolean(formattedValue));
        if (String(fieldElement.tagName || "").toUpperCase() === "IFRAME") {
          setSafeIframeSource(fieldElement, formattedValue);
          return;
        }
        if (String(fieldElement.tagName || "").toUpperCase() === "IMG") {
          setSafeImageSource(fieldElement, formattedValue);
          return;
        }
        if (fieldElement.tagName === "A") setSafeHref(fieldElement, formattedValue);
        if (styleBinding) {
          setTiquoStyle(fieldElement, styleBinding, formattedValue);
        } else if (!preserveText) {
          fieldElement.textContent = formattedValue;
        }
      });
      if (typeof afterRender === "function") afterRender(node, item);
      node.setAttribute("data-tiquo-rendered", "true");
      container.appendChild(node);
    });
  }

  function setSafeIframeSource(iframe, value) {
    try {
      var url = new URL(value, window.location.href);
      if (!parseTiquoEmbedTarget(url.toString())) {
        iframe.removeAttribute("src");
        return;
      }

      iframe.src = url.toString();
      iframe.setAttribute("src", url.toString());
      iframe.style.width = iframe.style.width || "100%";
      iframe.style.height = iframe.style.height || "200px";
      iframe.style.minHeight = iframe.style.minHeight || "200px";
      iframe.style.border = iframe.style.border || "none";
      iframe.style.display = "block";
      iframe.style.overflow = "hidden";
      iframe.style.transition =
        iframe.style.transition || "height 150ms ease";
      if (iframe.getAttribute("data-tiquo-auto-resize") === null) {
        iframe.setAttribute("data-tiquo-auto-resize", "true");
      }
      iframe.setAttribute("scrolling", "no");
      iframe.setAttribute("loading", "lazy");
      iframe.setAttribute("allow", "web-share");
      registerIframeAuthBridge(iframe);
    } catch {
      iframe.removeAttribute("src");
    }
  }

  function setTiquoStyle(element, property, value) {
    var normalized = String(property || "").trim().toLowerCase();
    if (normalized === "background-color") element.style.backgroundColor = value;
    else if (normalized === "border-color") element.style.borderColor = value;
    else if (normalized === "color") element.style.color = value;
    else if (normalized === "background-image") {
      var imageUrl = getSafeImageUrl(value);
      element.style.backgroundImage = imageUrl
        ? 'url("' + imageUrl.replace(/["\\]/g, "\\$&") + '")'
        : "";
    }
  }

  function clearRendered(container) {
    selectAll("[data-tiquo-rendered]", container).forEach(function (node) {
      node.parentNode && node.parentNode.removeChild(node);
    });
  }

  function selectAll(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function selectIncludingRoot(selector, root) {
    var elements = selectAll(selector, root);
    if (root && typeof root.matches === "function" && root.matches(selector)) {
      elements.unshift(root);
    }
    return elements;
  }

  // getPath reads a missing value as ""; this tells the two apart.
  function hasPathValue(object, path) {
    var value = object;
    var keys = String(path || "").split(".");
    for (var i = 0; i < keys.length; i++) {
      if (value === undefined || value === null) return false;
      value = value[keys[i]];
    }
    return value !== undefined && value !== null;
  }

  function getPath(object, path) {
    if (!object || !path) return "";
    return String(path)
      .split(".")
      .reduce(function (value, key) {
        return value && value[key] !== undefined && value[key] !== null ? value[key] : "";
      }, object);
  }

  function formatOrderField(order, field) {
    switch (field) {
      case "date":
      case "orderDate":
        return formatDate(order.createdAt);
      case "day":
      case "orderDay":
        return formatDatePart(order.createdAt, "day");
      case "month":
      case "orderMonth":
        return formatDatePart(order.createdAt, "month");
      case "year":
      case "orderYear":
        return formatDatePart(order.createdAt, "year");
      case "items":
      case "orderItems":
        return formatOrderItems(order.items);
      case "itemQuantity":
      case "orderItemQuantity":
      case "itemsQuantity":
      case "quantity":
        return getOrderItemQuantity(order.items);
      case "itemQuantityLabel":
      case "orderItemQuantityLabel":
        return formatItemQuantityLabel(getOrderItemQuantity(order.items));
      case "total":
      case "subtotal":
      case "taxTotal":
      case "discountTotal":
        return formatMoney(getPath(order, field), order.currency);
      default:
        return getPath(order, field);
    }
  }

  function formatBookingField(booking, field) {
    switch (field) {
      case "date":
      case "bookingDate":
        return booking.bookingDate || formatDate(booking.rawDate || booking.date, booking.timezone);
      case "day":
      case "bookingDay":
        return booking.bookingDay || formatDatePart(booking.rawDate || booking.date, "day", booking.timezone);
      case "month":
      case "bookingMonth":
        return booking.bookingMonth || formatDatePart(booking.rawDate || booking.date, "month", booking.timezone);
      case "year":
      case "bookingYear":
        return booking.bookingYear || formatDatePart(booking.rawDate || booking.date, "year", booking.timezone);
      case "items":
      case "bookingItems":
        return booking.bookingItems || formatBookingItems(booking);
      case "itemQuantity":
      case "bookingItemQuantity":
      case "itemsQuantity":
      case "quantity":
        return booking.bookingItemQuantity || getBookingItemQuantity(booking);
      case "itemQuantityLabel":
      case "bookingItemQuantityLabel":
        return booking.bookingItemQuantityLabel || formatItemQuantityLabel(getBookingItemQuantity(booking));
      case "id":
      case "bookingId":
        return booking.bookingId || booking.bookingNumber || booking.id;
      case "duration":
      case "bookingDuration":
        return booking.bookingDuration || formatDuration(booking.rawDuration || booking.duration);
      case "startTime":
      case "bookingStartTime":
        return booking.bookingStartTime || formatTime(booking.rawStartTime || booking.startTime, booking.timezone);
      case "endTime":
      case "bookingEndTime":
        return booking.bookingEndTime || formatTime(booking.rawEndTime || booking.endTime, booking.timezone);
      default:
        return getPath(booking, field);
    }
  }

  function formatMembershipField(membership, field) {
    return getPath(membership, field);
  }

  function formatMembershipPlanField(plan, field) {
    return getPath(plan, field);
  }

  function formatGuestFlowField(flow, field) {
    return getPath(flow, field);
  }

  function formatFlowServiceField(service, field) {
    return getPath(service, field);
  }

  function normalizeFlowServiceForRender(
    service,
    bookingConfigId,
    fallbackCurrency,
    categories,
  ) {
    if (!service) return {};
    var serviceImages = getImageList(
      [service.serviceImage || service.featuredImage].concat(service.images || []),
      MAX_GALLERY_IMAGES,
    );
    var categoryGallery = getFlowServiceCategoryGallery(service, categories);
    var currency = service.currency || fallbackCurrency || "GBP";
    var effectivePrice =
      service.effectivePrice !== undefined && service.effectivePrice !== null
        ? service.effectivePrice
        : service.price;
    var durationType = String(service.durationType || "minutes").toLowerCase();
    var singularDurationUnit = {
      minutes: "minute",
      hourly: "hour",
      nightly: "night",
      daily: "day",
    }[durationType] || durationType.replace(/s$/, "");
    var duration = Number(service.duration);
    var durationLabel = Number.isFinite(duration)
      ? duration +
        " " +
        singularDurationUnit +
        (duration === 1 ? "" : "s")
      : "";
    var priceLabel = "";
    if (
      String(service.priceType || "").toLowerCase() === "free" ||
      Number(effectivePrice) === 0
    ) {
      priceLabel = "Free";
    } else if (Number.isFinite(Number(effectivePrice))) {
      try {
        priceLabel = new Intl.NumberFormat(
          (document.documentElement && document.documentElement.lang) ||
            undefined,
          {
            style: "currency",
            currency: String(currency).toUpperCase(),
            maximumFractionDigits: 2,
          },
        ).format(Number(effectivePrice));
      } catch {
        priceLabel =
          String(currency).toUpperCase() +
          " " +
          Number(effectivePrice).toFixed(2);
      }
    }

    // Services where the customer chooses the booking length.
    var bookingLength = service.bookingLength;
    if (bookingLength && bookingLength.mode === "range") {
      durationLabel =
        formatBookingLengthMinutes(bookingLength.minMinutes) +
        " to " +
        formatBookingLengthMinutes(bookingLength.maxMinutes);
      if (priceLabel && priceLabel !== "Free") priceLabel += " / hour";
    } else if (
      bookingLength &&
      bookingLength.mode === "options" &&
      Array.isArray(bookingLength.options) &&
      bookingLength.options.length > 0
    ) {
      var lengths = bookingLength.options.map(function (option) {
        return Number(option.minutes);
      });
      durationLabel =
        formatBookingLengthMinutes(Math.min.apply(null, lengths)) +
        " to " +
        formatBookingLengthMinutes(Math.max.apply(null, lengths));
      var lowestPrice = Math.min.apply(
        null,
        bookingLength.options.map(function (option) {
          return Number(option.price);
        }),
      );
      if (Number.isFinite(lowestPrice) && lowestPrice > 0) {
        try {
          priceLabel =
            "From " +
            new Intl.NumberFormat(
              (document.documentElement && document.documentElement.lang) ||
                undefined,
              {
                style: "currency",
                currency: String(currency).toUpperCase(),
                maximumFractionDigits: 2,
              },
            ).format(lowestPrice);
        } catch {
          priceLabel =
            "From " + String(currency).toUpperCase() + " " + lowestPrice.toFixed(2);
        }
      }
    }

    var bookingUrl = new URL(
      "https://book.tiquo.app/embed/" +
        encodeURIComponent(bookingConfigId),
    );
    bookingUrl.searchParams.set("serviceId", service.id);
    bookingUrl.searchParams.set("lockService", "true");
    // "Show as locked": listed for this visitor, but only members can book.
    var accessRestricted = service.accessRestricted === true;

    return extend({}, service, {
      serviceId: service.id,
      accessRestricted: accessRestricted,
      accessLabel: accessRestricted ? "Membership required" : "",
      bookingConfigId: bookingConfigId,
      images: serviceImages,
      categoryGallery: categoryGallery,
      galleryImages: serviceImages.length > 0 ? serviceImages : categoryGallery,
      image:
        service.image ||
        service.serviceImage ||
        service.featuredImage ||
        (service.images && service.images[0]) ||
        service.categoryImage ||
        (service.categoryImages && service.categoryImages[0]) ||
        "",
      featuredImage:
        service.featuredImage ||
        service.serviceImage ||
        (service.images && service.images[0]) ||
        "",
      serviceImage:
        service.serviceImage ||
        service.featuredImage ||
        (service.images && service.images[0]) ||
        "",
      categoryImage:
        service.categoryImage ||
        (service.categoryImages && service.categoryImages[0]) ||
        "",
      currency: currency,
      effectivePrice: effectivePrice,
      priceLabel: priceLabel,
      durationLabel: durationLabel,
      tagsLabel: Array.isArray(service.tags)
        ? service.tags.join(", ")
        : "",
      categoryIdsLabel: Array.isArray(service.categoryIds)
        ? service.categoryIds.join(", ")
        : "",
      categoryName:
        service.categoryName ||
        (service.categoryNames && service.categoryNames[0]) ||
        "",
      categoryNames: Array.isArray(service.categoryNames)
        ? service.categoryNames
        : service.categoryName
          ? [service.categoryName]
          : [],
      categoryNamesLabel: Array.isArray(service.categoryNames)
        ? service.categoryNames.join(", ")
        : service.categoryName || "",
      categoryDescription:
        service.categoryDescription ||
        (service.categoryDescriptions && service.categoryDescriptions[0]) ||
        "",
      categoryDescriptions: Array.isArray(service.categoryDescriptions)
        ? service.categoryDescriptions
        : service.categoryDescription
          ? [service.categoryDescription]
          : [],
      bookingUrl: bookingUrl.toString(),
    });
  }

  function getImageList(values, limit) {
    var images = [];
    (Array.isArray(values) ? values : []).forEach(function (value) {
      var url = typeof value === "string" ? value.trim() : "";
      if (url && images.length < limit && images.indexOf(url) === -1) {
        images.push(url);
      }
    });
    return images;
  }

  // The gallery of the first flow category containing the service. Older
  // catalog responses only carry each category's primary image.
  function getFlowServiceCategoryGallery(service, categories) {
    var categoryIds = Array.isArray(service.categoryIds) ? service.categoryIds : [];
    var list = Array.isArray(categories) ? categories : [];
    for (var i = 0; i < categoryIds.length; i += 1) {
      for (var j = 0; j < list.length; j += 1) {
        var category = list[j];
        if (!category || String(category.id) !== String(categoryIds[i])) continue;
        var gallery = getImageList(
          [category.image].concat(category.images || []),
          MAX_GALLERY_IMAGES,
        );
        if (gallery.length > 0) return gallery;
      }
    }
    return getImageList(
      [service.categoryImage].concat(service.categoryImages || []),
      1,
    );
  }

  // List fields such as `images` bound to an image take their first entry.
  function imageBindingValue(fieldElement, value) {
    if (!Array.isArray(value)) return value;
    var isImageBinding =
      String(fieldElement.tagName || "").toUpperCase() === "IMG" ||
      String(fieldElement.getAttribute("data-tiquo-style") || "")
        .trim()
        .toLowerCase() === "background-image";
    return isImageBinding ? value[0] : value;
  }

  // [data-tiquo-service-images] repeats its [data-tiquo-service-image] child
  // once per gallery image (up to 15, or data-tiquo-limit) so sites can build
  // their own carousel. The attribute value picks the source: "service",
  // "category", or empty for the service images falling back to the
  // category's. data-tiquo-image-count on the container holds the count.
  function bindFlowServiceGalleries(root, service) {
    selectIncludingRoot("[data-tiquo-service-images]", root).forEach(
      function (container) {
        var template = getTemplate(container, "[data-tiquo-service-image]");
        if (!template) return;
        var source = String(
          container.getAttribute("data-tiquo-service-images") || "",
        )
          .trim()
          .toLowerCase();
        var images =
          source === "service"
            ? service.images
            : source === "category"
              ? service.categoryGallery
              : service.galleryImages;
        images = getImageList(
          images,
          readPositiveInteger(
            container.getAttribute("data-tiquo-limit"),
            MAX_GALLERY_IMAGES,
          ),
        );
        clearRendered(container);
        container.setAttribute("data-tiquo-image-count", String(images.length));
        if (container.getAttribute("data-tiquo-hide-empty") !== null) {
          setElementVisible(container, images.length > 0);
        }
        images.forEach(function (imageUrl, index) {
          var node = template.cloneNode(true);
          node.hidden = false;
          node.style.display = "";
          node.removeAttribute("aria-hidden");
          node.removeAttribute("data-tiquo-rendered-template");
          node.setAttribute("data-tiquo-image-index", String(index));
          var styleBinding = node.getAttribute("data-tiquo-style");
          if (String(node.tagName || "").toUpperCase() === "IMG") {
            setSafeImageSource(node, imageUrl);
          } else if (styleBinding) {
            setTiquoStyle(node, styleBinding, imageUrl);
          } else {
            selectAll("img", node).forEach(function (image) {
              setSafeImageSource(image, imageUrl);
            });
          }
          node.setAttribute("data-tiquo-rendered", "true");
          container.appendChild(node);
        });
      },
    );
  }

  function bindFlowServiceFields(root, service) {
    selectIncludingRoot("[data-tiquo-service]", root).forEach(
      function (fieldElement) {
        var field = fieldElement.getAttribute("data-tiquo-service");
        var formattedValue = formatValue(
          imageBindingValue(fieldElement, formatFlowServiceField(service, field)),
        );
        var hideWhenEmpty =
          fieldElement.getAttribute("data-tiquo-hide-empty") !== null;
        var preserveText =
          fieldElement.getAttribute("data-tiquo-preserve-text") !== null;
        var styleBinding = fieldElement.getAttribute("data-tiquo-style");
        if (hideWhenEmpty) {
          setElementVisible(fieldElement, Boolean(formattedValue));
        }
        if (String(fieldElement.tagName || "").toUpperCase() === "IFRAME") {
          setSafeIframeSource(fieldElement, formattedValue);
          return;
        }
        if (String(fieldElement.tagName || "").toUpperCase() === "IMG") {
          setSafeImageSource(fieldElement, formattedValue);
          return;
        }
        if (fieldElement.tagName === "A") {
          setSafeHref(fieldElement, formattedValue);
        }
        if (styleBinding) {
          setTiquoStyle(fieldElement, styleBinding, formattedValue);
        } else if (!preserveText) {
          fieldElement.textContent = formattedValue;
        }
      },
    );
  }

  // Locked ("Show as locked") vs bookable state for custom service cards:
  // data-tiquo-service-access="locked" | "available" elements show only in
  // that state, and the card root carries data-tiquo-access-restricted.
  function bindFlowServiceAccess(root, service) {
    var hasService = Boolean(service && service.id);
    var accessRestricted = hasService && service.accessRestricted === true;
    if (hasService) {
      root.setAttribute(
        "data-tiquo-access-restricted",
        accessRestricted ? "true" : "false",
      );
    } else {
      root.removeAttribute("data-tiquo-access-restricted");
    }
    selectIncludingRoot("[data-tiquo-service-access]", root).forEach(
      function (element) {
        var mode = element.getAttribute("data-tiquo-service-access");
        setElementVisible(
          element,
          hasService &&
            (mode === "locked"
              ? accessRestricted
              : mode === "available"
                ? !accessRestricted
                : true),
        );
      },
    );
  }

  function bindFlowServiceLinks(root, service, bookingConfigId) {
    selectIncludingRoot("[data-tiquo-service-link]", root).forEach(
      function (element) {
        var template =
          element.getAttribute("data-tiquo-service-link") ||
          "?tiquoServiceId={id}";
        var value = template.replace(
          /\{(id|slug|bookingConfigId)\}/g,
          function (_match, field) {
            var replacement =
              field === "bookingConfigId"
                ? bookingConfigId
                : service[field] || "";
            return encodeURIComponent(String(replacement));
          },
        );
        setSafeHref(element, value);
      },
    );
  }

  function normalizeGuestFlowForRender(flow) {
    if (!flow) return {};
    var maxGuests = Number(flow.maxGuestsPerSubmission);
    var validMaxGuests = Number.isFinite(maxGuests) && maxGuests > 0
      ? Math.floor(maxGuests)
      : 1;
    var availableGuests = Number(flow.availableGuestPassQuantity);
    var validAvailableGuests = Number.isFinite(availableGuests) && availableGuests > 0
      ? Math.floor(availableGuests)
      : 0;
    return extend({}, flow, {
      maxGuests: validMaxGuests,
      maxGuestsLabel:
        validMaxGuests === 1
          ? "Up to 1 guest"
          : "Up to " + validMaxGuests + " guests",
      availableGuests: validAvailableGuests,
      availableGuestsLabel:
        validAvailableGuests === 1
          ? "1 Guest Pass available"
          : validAvailableGuests + " Guest Passes available",
      isAvailable: validAvailableGuests > 0,
    });
  }

  // Memberships held in a connected system are read-only in Tiquo. Responses
  // from before the source field existed only contain Tiquo memberships.
  var MEMBERSHIP_SOURCE_LABELS = { hubspot: "HubSpot" };

  function getMembershipSource(membership) {
    return String((membership && membership.source) || "tiquo").toLowerCase();
  }

  function membershipMatchesSource(membership, source) {
    if (!source || source === "all") return true;
    var membershipSource = getMembershipSource(membership);
    if (source === "external") return membershipSource !== "tiquo";
    return membershipSource === source;
  }

  function normalizeMembershipForRender(membership) {
    if (!membership) return {};
    var source = getMembershipSource(membership);
    var isExternal = source !== "tiquo";
    var statusLabel = formatMembershipStatus(membership.status);
    var paymentStatusLabel = formatMembershipStatus(membership.paymentStatus);
    var startMonthYear = formatMonthYear(membership.startDate);
    var renewalMonthYear = formatMonthYear(membership.renewalDate);
    var endMonthYear = formatMonthYear(membership.endDate);
    var activeSinceLabel = startMonthYear ? "Active since " + startMonthYear : "";
    var renewalLabel = renewalMonthYear ? "Renews " + renewalMonthYear : "";
    var endLabel = endMonthYear ? "Ends " + endMonthYear : "";
    var summaryParts = [];

    if (String(membership.status || "").toLowerCase() === "active") {
      if (activeSinceLabel) summaryParts.push(activeSinceLabel);
      if (renewalLabel) summaryParts.push(renewalLabel);
      else if (endLabel) summaryParts.push(endLabel);
    } else if (String(membership.status || "").toLowerCase() === "scheduled") {
      if (startMonthYear) summaryParts.push("Starts " + startMonthYear);
    } else {
      if (statusLabel) summaryParts.push(statusLabel);
      if (endMonthYear) summaryParts.push("Ended " + endMonthYear);
      else if (startMonthYear) summaryParts.push("Since " + startMonthYear);
    }

    return extend({}, membership, {
      source: source,
      isExternal: isExternal,
      sourceLabel: isExternal ? MEMBERSHIP_SOURCE_LABELS[source] || formatMembershipStatus(source) : "",
      icon: getMembershipIcon(membership),
      statusLabel: statusLabel,
      paymentStatusLabel: paymentStatusLabel,
      startDateLabel: formatDate(membership.startDate),
      startMonthYear: startMonthYear,
      renewalDateLabel: formatDate(membership.renewalDate),
      renewalMonthYear: renewalMonthYear,
      endDateLabel: formatDate(membership.endDate),
      endMonthYear: endMonthYear,
      activeSinceLabel: activeSinceLabel,
      renewalLabel: renewalLabel,
      endLabel: endLabel,
      summary: summaryParts.join(" · "),
      priceLabel: formatMembershipPrice(membership),
      billingPeriodLabel: formatBillingPeriod(membership.billingPeriod),
      features: formatMembershipFeatures(membership.features),
      currentLabel: membership.isCurrent ? "Current" : "",
    });
  }

  function normalizeMembershipPlanForRender(plan) {
    if (!plan) return {};
    return extend({}, plan, {
      icon: getMembershipIcon(plan),
      priceLabel: formatMembershipPrice(plan),
      billingPeriodLabel: formatBillingPeriod(plan.billingPeriod),
      features: formatMembershipFeatures(plan.features),
      currentLabel: plan.isCurrent ? "Current" : "",
      popularLabel: plan.isPopular ? "Popular" : "",
    });
  }

  function getMembershipIcon(item) {
    var icon = String((item && item.icon) || "").trim();
    if (icon) return icon;
    var name = String((item && item.name) || "").trim();
    return name ? name.charAt(0).toUpperCase() : "";
  }

  function formatMembershipFeatures(features) {
    return Array.isArray(features) ? features.filter(Boolean).join(", ") : "";
  }

  function formatMembershipStatus(value) {
    return String(value || "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, function (letter) { return letter.toUpperCase(); });
  }

  function formatMonthYear(timestamp) {
    var date = toDate(timestamp);
    if (!date) return "";
    try {
      return new Intl.DateTimeFormat("en", {
        month: "short",
        year: "numeric",
      }).format(date);
    } catch {
      return date.toLocaleString("en", { month: "short" }) + " " + date.getFullYear();
    }
  }

  function formatBillingPeriod(value) {
    switch (String(value || "").toLowerCase()) {
      case "weekly":
        return "week";
      case "biweekly":
        return "2 weeks";
      case "monthly":
        return "month";
      case "quarterly":
        return "quarter";
      case "semi_annually":
        return "6 months";
      case "yearly":
        return "year";
      case "biennially":
        return "2 years";
      case "triennially":
        return "3 years";
      case "one_time":
        return "one-time";
      default:
        return String(value || "").replace(/_/g, " ");
    }
  }

  function formatMembershipPrice(item) {
    if (!item || item.type === "free" || Number(item.price) === 0) return "Free";
    var amount = Number(item.price);
    if (!Number.isFinite(amount)) return "";
    var currencyCode = String(item.currency || "GBP").trim().toUpperCase() || "GBP";
    var formatted;
    try {
      formatted = new Intl.NumberFormat("en", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch {
      formatted = currencyCode + " " + (amount / 100).toFixed(amount % 100 === 0 ? 0 : 2);
    }
    var period = formatBillingPeriod(item.billingPeriod);
    return formatted + (period && period !== "one-time" ? "/" + period : "");
  }

  function normalizeBookingForRender(booking) {
    if (!booking) return {};
    var displayId = booking.bookingNumber || booking.id || "";
    var itemQuantity = getBookingItemQuantity(booking);
    var bookingDate = formatDate(booking.date, booking.timezone);
    var bookingStartTime = formatTime(booking.startTime, booking.timezone);
    var bookingEndTime = formatTime(booking.endTime, booking.timezone);
    var bookingDuration = formatDuration(booking.duration);

    return extend({}, booking, {
      rawId: booking.id,
      rawDate: booking.date,
      rawStartTime: booking.startTime,
      rawEndTime: booking.endTime,
      rawDuration: booking.duration,
      id: displayId,
      bookingId: displayId,
      date: bookingDate,
      bookingDate: bookingDate,
      day: formatDatePart(booking.date, "day", booking.timezone),
      bookingDay: formatDatePart(booking.date, "day", booking.timezone),
      month: formatDatePart(booking.date, "month", booking.timezone),
      bookingMonth: formatDatePart(booking.date, "month", booking.timezone),
      year: formatDatePart(booking.date, "year", booking.timezone),
      bookingYear: formatDatePart(booking.date, "year", booking.timezone),
      items: formatBookingItems(booking),
      bookingItems: formatBookingItems(booking),
      itemQuantity: itemQuantity,
      bookingItemQuantity: itemQuantity,
      itemQuantityLabel: formatItemQuantityLabel(itemQuantity),
      bookingItemQuantityLabel: formatItemQuantityLabel(itemQuantity),
      duration: bookingDuration,
      bookingDuration: bookingDuration,
      startTime: bookingStartTime,
      bookingStartTime: bookingStartTime,
      endTime: bookingEndTime,
      bookingEndTime: bookingEndTime,
    });
  }

  function toDate(timestamp) {
    if (timestamp === undefined || timestamp === null || timestamp === "") return null;
    var value = Number(timestamp);
    if (!Number.isFinite(value)) return null;
    if (value > 0 && value < 10000000000) value = value * 1000;
    var date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function padNumber(value) {
    return String(value).padStart(2, "0");
  }

  function formatDate(timestamp, timeZone) {
    var date = toDate(timestamp);
    if (!date) return "";
    if (timeZone) {
      try {
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: timeZone,
        }).format(date);
      } catch {
        // Fall through to local formatting when the stored timezone is invalid.
      }
    }
    return [
      padNumber(date.getDate()),
      padNumber(date.getMonth() + 1),
      date.getFullYear(),
    ].join("/");
  }

  function formatDatePart(timestamp, part, timeZone) {
    var date = toDate(timestamp);
    if (!date) return "";
    if (timeZone) {
      try {
        if (part === "day") {
          return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            timeZone: timeZone,
          }).format(date);
        }
        if (part === "month") {
          return new Intl.DateTimeFormat("en", {
            month: "short",
            timeZone: timeZone,
          }).format(date).toUpperCase();
        }
        if (part === "year") {
          return new Intl.DateTimeFormat("en-GB", {
            year: "numeric",
            timeZone: timeZone,
          }).format(date);
        }
      } catch {
        // Fall through to local formatting when the stored timezone is invalid.
      }
    }
    if (part === "day") return padNumber(date.getDate());
    if (part === "month") {
      return date.toLocaleString("en", { month: "short" }).toUpperCase();
    }
    if (part === "year") return String(date.getFullYear());
    return "";
  }

  function formatTime(value, timeZone) {
    if (value === undefined || value === null || value === "") return "";
    var numeric = Number(value);
    if (Number.isFinite(numeric)) {
      var numericDate = toDate(numeric);
      if (!numericDate) return "";
      return formatDateTimePart(numericDate, timeZone);
    }

    var text = String(value).trim();
    var timeMatch = text.match(/^(\d{1,2}):(\d{2})/);
    if (timeMatch) return padNumber(timeMatch[1]) + ":" + timeMatch[2];

    var parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return formatDateTimePart(parsed, timeZone);
    return text;
  }

  function formatDateTimePart(date, timeZone) {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: timeZone || undefined,
      }).format(date);
    } catch {
      return padNumber(date.getHours()) + ":" + padNumber(date.getMinutes());
    }
  }

  function formatDuration(value) {
    var minutes = Number(value);
    if (!Number.isFinite(minutes)) return "";
    minutes = Math.round(minutes);
    if (minutes < 60) return minutes + " " + (minutes === 1 ? "min" : "min");
    var hours = Math.floor(minutes / 60);
    var remainingMinutes = minutes % 60;
    return hours + " " + (hours === 1 ? "hr" : "hrs") +
      (remainingMinutes ? " " + remainingMinutes + " min" : "");
  }

  function formatOrderItems(items) {
    if (!Array.isArray(items)) return "";
    return items
      .map(function (item) { return item && item.name ? String(item.name) : ""; })
      .filter(Boolean)
      .join(", ");
  }

  function getOrderItemQuantity(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce(function (total, item) {
      var quantity = Number(item && item.quantity);
      return total + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
  }

  function formatBookingItems(booking) {
    if (booking && Array.isArray(booking.items)) return formatOrderItems(booking.items);
    return booking && booking.serviceName ? String(booking.serviceName) : "";
  }

  function getBookingItemQuantity(booking) {
    if (booking && Array.isArray(booking.items)) return getOrderItemQuantity(booking.items);
    var attendeeCount = Number(booking && booking.attendeeCount);
    return Number.isFinite(attendeeCount) ? attendeeCount : 0;
  }

  function formatItemQuantityLabel(quantity) {
    return quantity + " " + (quantity === 1 ? "item" : "items");
  }

  function formatMoney(value, currency) {
    var amount = Number(value);
    if (!Number.isFinite(amount)) return "";
    var currencyCode = String(currency || "GBP").trim().toUpperCase() || "GBP";
    try {
      return new Intl.NumberFormat("en", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return currencyCode + " " + amount.toFixed(2);
    }
  }

  function getCustomerParameterValue(customer, parameter) {
    if (!customer || !parameter) return "";
    var key = normalizeParameterKey(parameter);
    var item = null;
    var parameters = customer.parameters || [];
    for (var i = 0; i < parameters.length; i += 1) {
      if (
        parameters[i].id === parameter ||
        parameters[i].name === parameter ||
        parameters[i].key === key ||
        normalizeParameterKey(parameters[i].name) === key
      ) {
        item = parameters[i];
        break;
      }
    }

    var values = customer.parameterValues || {};
    var value = values[parameter] !== undefined
      ? values[parameter]
      : values[key] !== undefined
        ? values[key]
        : item ? item.value : "";
    // Multiselect values are stored as JSON arrays; parse them so they render as a list.
    return item && item.type === "multiselect" ? parseMultiselectValue(value) : value;
  }

  function normalizeParameterKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  function formatValue(value) {
    if (value === undefined || value === null) return "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "number") return String(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  function formatCustomerFieldValue(element, field, value) {
    var dateFormat = String(element.getAttribute("data-tiquo-date-format") || "")
      .trim()
      .toLowerCase();
    if (!dateFormat || (field !== "createdAt" && field !== "firstActiveAt")) {
      return formatValue(value);
    }
    if (value === undefined || value === null || value === "") return "";

    var timestamp = Number(value);
    if (!Number.isFinite(timestamp)) return "";
    var date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";

    if (dateFormat === "raw") return String(timestamp);
    if (dateFormat === "iso") return date.toISOString();
    if (dateFormat !== "date" && dateFormat !== "datetime") {
      return formatValue(value);
    }

    var locale =
      element.getAttribute("data-tiquo-locale") ||
      (document.documentElement && document.documentElement.lang) ||
      navigator.language ||
      undefined;
    var timeZone = element.getAttribute("data-tiquo-time-zone") || undefined;
    var options = dateFormat === "datetime"
      ? { dateStyle: "medium", timeStyle: "short" }
      : { dateStyle: "medium" };
    if (timeZone) options.timeZone = timeZone;

    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return formatValue(value);
    }
  }

  function setSafeHref(element, value) {
    if (!value) {
      element.removeAttribute("href");
      return;
    }

    try {
      var url = new URL(String(value), window.location.href);
      if (/^(https?:|mailto:|tel:)$/i.test(url.protocol)) {
        element.href = url.href;
        return;
      }
    } catch {
      // Fall through to clearing the attribute.
    }

    element.removeAttribute("href");
  }

  function getSafeImageUrl(value) {
    if (!value) return "";
    try {
      var url = new URL(String(value), window.location.href);
      return /^(https?:)$/i.test(url.protocol) ? url.href : "";
    } catch {
      return "";
    }
  }

  function setSafeImageSource(element, value) {
    var imageUrl = getSafeImageUrl(value);
    if (imageUrl) element.setAttribute("src", imageUrl);
    else element.removeAttribute("src");
  }

  function extend(target) {
    target = target || {};
    for (var i = 1; i < arguments.length; i += 1) {
      var source = arguments[i] || {};
      Object.keys(source).forEach(function (key) {
        if (source[key] !== undefined) target[key] = source[key];
      });
    }
    return target;
  }
})();
